import { appAlert } from '../../../services/appDialogs';
import { DOMESTIC_SEEDANCE_DEFAULT_MODEL, isDomesticOfficialSeedance, getDomesticSeedanceResolutions, normalizeDomesticSeedanceResolution } from '../../../utils/domesticSeedanceCapabilities';
import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Check, Film, ChevronDown, ChevronRight, User, MapPin, Box, Image as ImageIcon, Plus, Trash2, AlertCircle, Upload, Video, FolderOpen, Drama, Mountain, Wrench, Clapperboard, Palette, Lightbulb, Flame, Zap, CloudRain, Eye, Flashlight, Sunset, Sun, Sparkles, Gem, Feather, Moon, Home } from "lucide-react";
import { VIDEO_MODELS } from "./Constants";
import { useTheme } from "@context/ThemeContext";
import { useToast } from "@context/ToastContext";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import ResourceSelector from "../../resource/ResourceSelector";
import { uploadFileToCos } from "../../../utils/fileUtils";
import { serializeEditor, stripRefMarkers, createMentionChip, renderPromptToEditor } from "../../../utils/mentionUtils";
import { LIGHTING_PRESETS } from "./LightingPresets";
import {
  getGuaOminiVideoResolutions,
  getProjectVideoProvider,
  getVideoProviderLabel,
  isSeedanceVideoProvider,
  isWan3Project,
  normalizeVideoResolutionForProvider,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
  VIDEO_PROVIDER_WANX,
} from "../../../utils/projectRuntime";
import { getEffectiveAssetMeta } from "../../../utils/guaVideoAssetStatus";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import {
  assertWanxVideoMediaSelection,
  createWanxVideoMediaItem,
  getWanxVideoMediaLabel,
  inferWanxVideoReferenceType,
  isWanxVideoModel,
  normalizeWanxVideoMedia,
  normalizeWanxVideoModel,
  validateWanxVideoMediaFile,
  WANX_VIDEO_ACCEPT,
  WANX_VIDEO_MEDIA_TYPES,
  WANX_VIDEO_MODEL,
  WANX_VIDEO_MODELS,
} from "../../../utils/wanxVideoCapabilities";


const VIDU_MODEL = VIDEO_MODELS.find(m => m.id === "vidu");
const VIDU_SUB_MODELS = VIDU_MODEL?.subModels || [];

const WANGSUSORA_MODEL = VIDEO_MODELS.find(m => m.id === "wangsuSora");
const WANGSUSORA_SUB_MODELS = WANGSUSORA_MODEL?.subModels || [];

const KLING_MODEL = VIDEO_MODELS.find(m => m.id === "kling");
const KLING_SUB_MODELS = KLING_MODEL?.subModels || [];
const isGuaLikeModel = isSeedanceVideoProvider;
const MENTION_RENDER_LIMIT = 80;

const getStoredVideoProvider = (scene = {}) => {
  const raw = scene.video_provider || scene.videoProvider || scene.video_model || scene.videoModel || "vidu";
  return isWanxVideoModel(raw) ? "wanx" : raw;
};

const MOVEMENT_OPTIONS = [
  { value: "auto", label: "自动" },
  { value: "small", label: "小" },
  { value: "medium", label: "中" },
  { value: "large", label: "大" },
];

const AUDIO_TYPE_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "speech_only", label: "仅语音" },
  { value: "sound_effect_only", label: "仅音效" },
];

// Toggle button helper
const Toggle = ({ value, onChange, disabled }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <button
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      className="relative w-9 h-5 rounded-full transition-colors shrink-0"
      style={{
        backgroundColor: value && !disabled ? theme.colors.bluePrimary : 'var(--color-bg-tertiary)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full transition-transform"
        style={{
          backgroundColor: 'white',
          left: value && !disabled ? '18px' : '2px',
        }}
      />
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <label className="text-xs font-bold tracking-wider flex items-center gap-1.5" style={{ color: 'var(--color-text-secondary)' }}>
    {children}
  </label>
);

// 打光预设图标（替代 emoji，按预设语义就近选择）
const LIGHTING_ICON_MAP = {
  standard: Lightbulb,
  romantic: Flame,
  conflict: Zap,
  sad: CloudRain,
  fear: Eye,
  suspense: Flashlight,
  sunset: Sunset,
  morning: Sun,
  rembrandt: Palette,
  butterfly: Sparkles,
  'beauty-tyndall-rim': Sparkles,
  'beauty-paramount-glow': Gem,
  'beauty-cream-soft': Feather,
  'cold-moon': Moon,
  'warm-indoor': Home,
};

// 参考图预览区
const RefImageSection = ({ scene, characterOptions, sceneOptions, propOptions, preferredProvider }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const resolveRefs = (ids, options, variantIndexes) => {
    if (!ids?.length || !options?.length) return [];
    return ids.map((id, i) => {
      const item = options.find(opt => opt.id === id);
      if (!item) return null;
      const vi = variantIndexes?.[i] ?? -1;
      let imageUrl = item.imageUrl;
      let selectedAsset = item;
      if (vi >= 0 && item.variations?.[vi]?.imageUrl) {
        imageUrl = item.variations[vi].imageUrl;
        selectedAsset = item.variations[vi];
      }
      const assetStatus = getEffectiveAssetMeta(selectedAsset, preferredProvider).status;
      return imageUrl ? { name: item.name, imageUrl, variantIndex: vi, assetStatus } : null;
    }).filter(Boolean);
  };

  const charRefs = resolveRefs(scene.refCharacterIds, characterOptions, scene.refCharacterVariantIndexes);
  const sceneRefs = resolveRefs(scene.refSceneIds, sceneOptions, scene.refSceneVariantIndexes);
  const propRefs = resolveRefs(scene.refPropIds, propOptions, scene.refPropVariantIndexes);

  const totalRefs = charRefs.length + sceneRefs.length + propRefs.length;
  if (totalRefs === 0) {
    return (
      <div className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px dashed var(--color-border-primary)' }}>
        <ImageIcon size={20} className="mx-auto mb-1.5" style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>未关联参考图</p>
        <p className="text-2xs mt-0.5" style={{ color: 'var(--color-text-muted)', opacity: 0.6 }}>在左侧控制栏选择角色/场景/道具</p>
      </div>
    );
  }

  const RefGroup = ({ label, icon: Icon, refs, color }) => {
    if (!refs.length) return null;
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Icon size={12} style={{ color }} />
          <span className="text-2xs font-bold" style={{ color }}>{label}</span>
          <span className="text-2xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${color}20`, color }}>{refs.length}</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {refs.map((ref, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <div
                className="rounded-lg overflow-hidden border-2 transition-all relative"
                style={{ borderColor: ref.assetStatus === "Active" ? '#22c55e' : `${color}40`, backgroundColor: 'var(--color-bg-overlay)', height: '56px' }}
              >
                <img src={ref.imageUrl} className="h-full w-auto object-contain" alt={ref.name} />
                {ref.assetStatus === "Active" && (
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-px" style={{ backgroundColor: 'color-mix(in srgb, var(--color-success) 85%, transparent)' }}>
                    <Check size={7} color="white" strokeWidth={3} />
                  </div>
                )}
              </div>
              <span className="text-2xs font-medium text-center max-w-[56px] truncate" style={{ color: 'var(--color-text-secondary)' }}>
                {ref.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <RefGroup label="角色" icon={User} refs={charRefs} color={theme.colors.accentPrimary} />
      <RefGroup label="场景" icon={MapPin} refs={sceneRefs} color={theme.colors.bluePrimary} />
      <RefGroup label="道具" icon={Box} refs={propRefs} color={theme.colors.purplePrimary} />
    </div>
  );
};


const VideoSettingsPanel = ({ scene, index, rows, onClose, handleChange, className, onOpenMultiShot, characterOptions, sceneOptions, propOptions, handleGenerateVideo, projectId, episodeId, exiting }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const [isEndFrameSelectorOpen, setIsEndFrameSelectorOpen] = useState(false);
  const [hoveredFrameImage, setHoveredFrameImage] = useState(null);
  const [hoveredFramePos, setHoveredFramePos] = useState({ top: 0, left: 0 });
  const [atPreview, setAtPreview] = useState(null); // @ 菜单选中项大图预览
  const panelRef = useRef(null);
  const guaImageUploadRef = useRef(null);
  const [showGuaResourceSelector, setShowGuaResourceSelector] = useState(false);
  const [guaImageUploading, setGuaImageUploading] = useState(false);
  const videoPromptEditorRef = useRef(null);
  const [vpMentionPopover, setVpMentionPopover] = useState(null); // { x, y, searchText, level, path, category, asset }
  const vpMentionIndexRef = useRef(0);
  const [vpMenuItems, setVpMenuItems] = useState([]);
  const [vpMenuLoading, setVpMenuLoading] = useState(false);
  const [vpRefNames, setVpRefNames] = useState(new Map()); // url → name 映射
  const [projectRuntimeTick, setProjectRuntimeTick] = useState(0);
  const vpMentionUploadRef = useRef(null);
  const vpMentionVideoUploadRef = useRef(null);
  const vpMentionItemRefs = useRef([]);
  const vpAtNodeRef = useRef(null); // @ 所在的文本节点
  const vpAtIndexRef = useRef(-1); // @ 在文本节点中的 index

  // ── 多镜头编辑内嵌状态 ──
  const [showMultiShotEditor, setShowMultiShotEditor] = useState(false);
  const [focusedShotIdx, setFocusedShotIdx] = useState(null);
  const msTextareaRefs = useRef({});

  // 出场动画：尾帧选择下拉 / 多镜头编辑区延迟卸载
  const endFrameAnim = useDelayedUnmount(isEndFrameSelectorOpen, 150);
  const multiShotEditorAnim = useDelayedUnmount(showMultiShotEditor, 150);

  const handleFrameHover = (e, imageUrl) => {
    if (!imageUrl) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    const previewW = 320;
    let left = (panelRect?.left || rect.left) - previewW - 10;
    if (left < 10) left = (panelRect?.right || rect.right) + 10;
    let top = rect.top - 40;
    if (top + 220 > window.innerHeight) top = window.innerHeight - 230;
    if (top < 10) top = 10;
    setHoveredFrameImage(imageUrl);
    setHoveredFramePos({ top, left });
  };

  useEffect(() => {
    const onProfileChange = (event) => {
      if (!projectId || String(event?.detail?.projectId) === String(projectId)) {
        setProjectRuntimeTick((value) => value + 1);
      }
    };
    window.addEventListener('project-runtime-profile-change', onProfileChange);
    return () => window.removeEventListener('project-runtime-profile-change', onProfileChange);
  }, [projectId]);

  const defaultModelId = "vidu";
  const projectGuaModelId = useMemo(
    () => getProjectVideoProvider(projectId, "guavideo"),
    [projectId, projectRuntimeTick],
  );
  const wan3Project = projectGuaModelId === VIDEO_PROVIDER_WANX || isWan3Project(projectId);
  const storedProvider = getStoredVideoProvider(scene);
  const storedModelId = VIDEO_MODELS?.some(m => m.id === storedProvider) ? storedProvider : defaultModelId;
  const selectedModelId = wan3Project
    ? VIDEO_PROVIDER_WANX
    : !isDomesticOfficialSeedance(storedModelId) && isGuaLikeModel(storedModelId) && isGuaLikeModel(projectGuaModelId)
      ? projectGuaModelId
      : storedModelId;
  const visibleVideoModels = wan3Project
    ? VIDEO_MODELS.filter((model) => model.id === VIDEO_PROVIDER_WANX)
    : VIDEO_MODELS.filter((model) => (
      !isGuaLikeModel(model.id) || isDomesticOfficialSeedance(model.id) || model.id === projectGuaModelId
    ));
  const effectiveGuaModelId = isGuaLikeModel(selectedModelId) && !isDomesticOfficialSeedance(selectedModelId) ? projectGuaModelId : selectedModelId;
  const currentModel = VIDEO_MODELS?.find(m => m.id === selectedModelId) || (VIDEO_MODELS?.[0] || { durations: [], defaultDuration: '5s' });

  const isVidu = selectedModelId === "vidu";
  const isWangsuSora = selectedModelId === "wangsuSora";
  const isKling = false;
  const isGuaVideo = isGuaLikeModel(selectedModelId);
  const isWanx = selectedModelId === "wanx";
  const storedWanxModel = scene.video_model || scene.videoModel;
  const currentWanxModel = normalizeWanxVideoModel(
    scene.video_sub_model || scene.videoSubModel || (isWanxVideoModel(storedWanxModel) ? storedWanxModel : null),
  );

  useEffect(() => {
    if (!wan3Project) return;

    const persistedProvider = scene.video_provider || scene.videoProvider;
    const persistedTopLevelModel = scene.video_model || scene.videoModel;
    const persistedSubModel = scene.video_sub_model || scene.videoSubModel;
    if (persistedProvider !== VIDEO_PROVIDER_WANX) {
      handleChange(index, "video_provider", VIDEO_PROVIDER_WANX);
    }
    if (persistedTopLevelModel !== VIDEO_PROVIDER_WANX) {
      handleChange(index, "video_model", VIDEO_PROVIDER_WANX);
    }
    if (!isWanxVideoModel(persistedSubModel)) {
      handleChange(index, "video_sub_model", currentWanxModel);
    }
  }, [
    currentWanxModel,
    handleChange,
    index,
    scene.video_model,
    scene.video_provider,
    scene.video_sub_model,
    scene.videoModel,
    scene.videoProvider,
    scene.videoSubModel,
    wan3Project,
  ]);
  const activeGuaMode = isGuaVideo ? (scene.video_gua_mode || "ref2v") : null;
  const activeWanxMode = isWanx ? (scene.video_wan_mode || "first_frame") : null;
  const guaSubModels = VIDEO_MODELS.find(m => m.id === effectiveGuaModelId)?.subModels || [];
  const guaResolutionOptions = isDomesticOfficialSeedance(effectiveGuaModelId)
    ? getDomesticSeedanceResolutions(scene.video_sub_model)
    : effectiveGuaModelId === VIDEO_PROVIDER_OMINI_SEEDANCE
    ? getGuaOminiVideoResolutions()
    : ["480p", "720p"];
  const currentGuaResolution = isDomesticOfficialSeedance(effectiveGuaModelId) ? normalizeDomesticSeedanceResolution(scene.video_sub_model, scene.video_gua_resolution) : normalizeVideoResolutionForProvider(
    effectiveGuaModelId,
    scene.video_gua_resolution,
    "720p",
  );
  const effectiveGuaDuration = scene.video_gua_duration ?? 5;

  const handleGuaModeChange = useCallback((guaMode) => {
    handleChange(index, "video_gua_mode", guaMode);
  }, [handleChange, index]);

  const wanxReferenceMedia = useMemo(
    () => normalizeWanxVideoMedia(scene.video_wan_media).filter((item) => ![
      WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME,
      WANX_VIDEO_MEDIA_TYPES.LAST_FRAME,
    ].includes(item.type)),
    [scene.video_wan_media],
  );

  const addWanxReferenceMedia = useCallback((rawItem) => {
    if (!isWanx || activeWanxMode !== "reference_to_video") {
      toast.warning("请先切换到万相3.0参考生视频模式");
      return false;
    }
    const item = rawItem?.id ? rawItem : createWanxVideoMediaItem(rawItem);
    if (wanxReferenceMedia.some((candidate) => candidate.type === item.type && candidate.url === item.url)) {
      toast.warning("该参考素材已经添加");
      return false;
    }
    try {
      const nextMedia = assertWanxVideoMediaSelection(
        [...wanxReferenceMedia, item],
        "reference",
        scene.video_wan_duration ?? 5,
      );
      handleChange(index, "video_wan_media", nextMedia);
      setVpRefNames((previous) => new Map(previous).set(item.url, item.name || getWanxVideoMediaLabel(item, nextMedia)));
      return true;
    } catch (error) {
      toast.warning(error.message);
      return false;
    }
  }, [activeWanxMode, handleChange, index, isWanx, scene.video_wan_duration, toast, wanxReferenceMedia]);

  const removeWanxReferenceMedia = useCallback((mediaId) => {
    handleChange(index, "video_wan_media", wanxReferenceMedia.filter((item) => item.id !== mediaId));
  }, [handleChange, index, wanxReferenceMedia]);

  const handleWanxModeChange = useCallback((wanxMode) => {
    handleChange(index, "video_wan_mode", wanxMode);
    if (wanxMode !== "reference_to_video" && wanxReferenceMedia.length > 0) {
      handleChange(index, "video_wan_media", []);
    }
  }, [handleChange, index, wanxReferenceMedia.length]);

  // ── @ 提及：数据源（GuaVideo / 万相参考媒体）──
  const vpMentionSource = useMemo(() => {
    if (isGuaVideo) {
      return [
        ...(scene.video_gua_ref_images || []).map((url, i) => ({ id: `gua-image-${i}`, name: `图片${i + 1}`, url, category: 'image' })),
        ...(scene.video_gua_ref_videos || []).map((url, i) => ({ id: `gua-video-${i}`, name: `视频${i + 1}`, url, category: 'video' })),
      ];
    }
    if (isWanx && activeWanxMode === "reference_to_video") {
      return wanxReferenceMedia.map((item) => ({
        id: item.id,
        name: item.name || getWanxVideoMediaLabel(item, wanxReferenceMedia),
        url: item.url,
        category: item.type,
      }));
    }
    return [];
  }, [activeWanxMode, isGuaVideo, isWanx, scene.video_gua_ref_images, scene.video_gua_ref_videos, wanxReferenceMedia]);

  const vpFilteredMentions = useMemo(() => {
    if (!vpMentionPopover) return [];
    const q = vpMentionPopover.searchText || '';
    if (vpMentionPopover.level === 'root') {
      const existingItems = isGuaVideo
        ? [
          ...(scene.video_gua_ref_images || []).map((url, i) => ({
            id: `existing-image-${i}`, icon: null, imageUrl: url, url,
            label: vpRefNames.get(url) || `图片${i + 1}`,
            action: 'select-existing',
            category: 'image',
          })),
          ...(scene.video_gua_ref_videos || []).map((url, i) => ({
            id: `existing-video-${i}`, icon: Video, imageUrl: null, url,
            label: vpRefNames.get(url) || `视频${i + 1}`,
            action: 'select-existing',
            category: 'video',
          })),
        ]
        : isWanx && activeWanxMode === "reference_to_video"
          ? wanxReferenceMedia.map((item) => ({
            id: `existing-wanx-${item.id}`,
            icon: item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO ? Video : null,
            imageUrl: item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE ? item.url : null,
            url: item.url,
            label: item.name || getWanxVideoMediaLabel(item, wanxReferenceMedia),
            action: 'select-existing',
            category: item.type,
          }))
          : [];
      const canAddWanxReference = !isWanx || activeWanxMode === "reference_to_video";
      const rootItems = canAddWanxReference ? [
          ...existingItems,
          { id: 'resource', icon: FolderOpen, label: '资源管理器', action: 'navigate', nextLevel: 'source' },
          { id: 'upload-image', icon: ImageIcon, label: '上传图片', action: 'upload-image', searchText: '图片 image upload' },
          { id: 'upload-video', icon: Video, label: '上传视频', action: 'upload-video', searchText: '视频 video upload' },
        ] : [];
      const matches = (item) => `${item.label || ''} ${item.searchText || ''}`.toLowerCase().includes(q);
      return q ? rootItems.filter(matches) : rootItems;
    }
    if (['source', 'assets', 'variants'].includes(vpMentionPopover.level)) {
      return q ? vpMenuItems.filter(i => i.label.toLowerCase().includes(q)) : vpMenuItems;
    }
    return vpMentionSource.filter(m => !q || m.name.toLowerCase().includes(q));
  }, [activeWanxMode, isGuaVideo, isWanx, scene.video_gua_ref_images, scene.video_gua_ref_videos, vpMentionPopover, vpMentionSource, vpMenuItems, vpRefNames, wanxReferenceMedia]);

  const vpVisibleMentions = useMemo(() => vpFilteredMentions.slice(0, MENTION_RENDER_LIMIT), [vpFilteredMentions]);
  const vpHiddenMentionCount = Math.max(0, vpFilteredMentions.length - vpVisibleMentions.length);
  const vpFilteredRef = useRef(vpVisibleMentions);
  vpFilteredRef.current = vpVisibleMentions;

  // 出场动画：@ 提及菜单延迟卸载（缓存 vpMentionPopover 供退场渲染）
  const vpMenuOpen = !!(vpMentionPopover && (vpFilteredMentions.length > 0 || vpMenuLoading));
  const vpMenuAnim = useDelayedUnmount(vpMenuOpen, 150);
  const lastVpPopoverRef = useRef(null);
  if (vpMentionPopover) lastVpPopoverRef.current = vpMentionPopover;

  // 序列化 editor（带标记）
  const getVpSerialized = useCallback(() => serializeEditor(videoPromptEditorRef.current), []);

  // 往 editor 末尾追加 chip（非 @ 方式添加参考图时调用）
  const appendChipToEditor = useCallback((name, url, category = '') => {
    const el = videoPromptEditorRef.current;
    if (!el) return;
    const chip = createMentionChip(name, url, 'var(--color-purple-primary)', category);
    const lastChild = el.lastChild;
    if (lastChild && lastChild.nodeType === Node.TEXT_NODE && !lastChild.textContent.endsWith(' ')) {
      el.appendChild(document.createTextNode(' '));
    } else if (!lastChild || lastChild.nodeType !== Node.TEXT_NODE) {
      el.appendChild(document.createTextNode(' '));
    }
    el.appendChild(chip);
    el.appendChild(document.createTextNode('\u00A0'));
    handleChange(index, "video_prompt", getVpSerialized());
  }, [index, handleChange, getVpSerialized]);

  // 更新 @ 菜单选中项（不触发重渲染）
  const updateVpMentionIndex = useCallback((newIndex) => {
    const oldIndex = vpMentionIndexRef.current;
    vpMentionIndexRef.current = newIndex;
    const oldItem = vpMentionItemRefs.current[oldIndex];
    const newItem = vpMentionItemRefs.current[newIndex];
    if (oldItem) {
      oldItem.style.backgroundColor = 'transparent';
      oldItem.style.color = 'var(--color-text-secondary)';
    }
    if (newItem) {
      newItem.style.backgroundColor = '#8b5cf618';
      newItem.style.color = 'var(--color-purple-primary)';
      newItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const imgUrl = newItem.dataset.imageUrl;
      setAtPreview(imgUrl ? { url: imgUrl } : null);
    } else {
      setAtPreview(null);
    }
  }, []);

  useEffect(() => {
    if (!vpMentionPopover) setAtPreview(null);
  }, [vpMentionPopover]);

  // 同步参考图变化到 editor chips（只做删除同步，不再覆盖 name）
  useEffect(() => {
    if (!videoPromptEditorRef.current) return;
    const chips = videoPromptEditorRef.current.querySelectorAll('[data-mention]');
    if (chips.length === 0) return;
    const urlSet = new Set(vpMentionSource.map(m => m.url));
    let changed = false;
    chips.forEach(chip => {
      if (!urlSet.has(chip.dataset.url)) { chip.remove(); changed = true; }
    });
    if (changed) handleChange(index, "video_prompt", getVpSerialized());
  }, [vpMentionSource]);

  const vpEditingRef = useRef(false);

  // 检测 @
  const handleVpEditorInput = useCallback(() => {
    vpEditingRef.current = true;
    handleChange(index, "video_prompt", getVpSerialized());

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { setVpMentionPopover(null); return; }
    const range = sel.getRangeAt(0);
    if (range.startContainer.nodeType !== Node.TEXT_NODE) { setVpMentionPopover(null); return; }
    const textBefore = range.startContainer.textContent.substring(0, range.startOffset);
    const lastAt = textBefore.lastIndexOf('@');
    if (lastAt !== -1) {
      const afterAt = textBefore.substring(lastAt + 1);
      if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
        const rect = videoPromptEditorRef.current.getBoundingClientRect();
        setVpMentionPopover({ x: rect.left + 10, y: rect.top, searchText: afterAt.toLowerCase(), level: 'root', path: [] });
        vpAtNodeRef.current = range.startContainer;
        vpAtIndexRef.current = lastAt;
        vpMentionIndexRef.current = 0;
        setVpMenuItems([]);
        return;
      }
    }
    setVpMentionPopover(null);
  }, [getVpSerialized, index, handleChange]);

  // 选中插入 chip
  const handleVpMentionSelect = useCallback((item) => {
    if (!vpMentionPopover || !videoPromptEditorRef.current) return;

    const atNode = vpAtNodeRef.current;
    const atIdx = vpAtIndexRef.current;

    if (!atNode || atIdx < 0 || !atNode.parentNode) {
      setVpMentionPopover(null);
      vpAtNodeRef.current = null;
      vpAtIndexRef.current = -1;
      return;
    }

    // 只删除 @ + 搜索文字，不多删
    const searchLen = (vpMentionPopover.searchText || '').length;
    const endOffset = Math.min(atIdx + 1 + searchLen, (atNode.textContent || '').length);

    const deleteRange = document.createRange();
    deleteRange.setStart(atNode, atIdx);
    deleteRange.setEnd(atNode, endOffset);
    deleteRange.deleteContents();
    const chip = createMentionChip(item.name, item.url, 'var(--color-purple-primary)', item.category || '');
    const afterNode = atNode.textContent.length > atIdx ? atNode.splitText(atIdx) : null;
    if (afterNode) {
      atNode.parentNode.insertBefore(chip, afterNode);
    } else if (atNode.parentNode) {
      atNode.parentNode.insertBefore(chip, atNode.nextSibling);
    } else {
      videoPromptEditorRef.current.appendChild(chip);
    }
    const space = document.createTextNode('\u00A0');
    chip.after(space);
    const sel = window.getSelection();
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    vpAtNodeRef.current = null;
    vpAtIndexRef.current = -1;
    setVpMentionPopover(null);
    handleChange(index, "video_prompt", getVpSerialized());
  }, [vpMentionPopover, index, handleChange, getVpSerialized]);

  // ── @ 多级菜单：导航 ──
  const handleVpMenuNavigate = useCallback(async (item) => {
    if (!vpMentionPopover) return;
    const prev = vpMentionPopover;

    if (item.action === 'upload' || item.action === 'upload-image') {
      vpMentionUploadRef.current?.click();
      return;
    }
    if (item.action === 'upload-video') {
      vpMentionVideoUploadRef.current?.click();
      return;
    }

    if (item.action === 'navigate') {
      const nextLevel = item.nextLevel;
      const nextPath = [...prev.path, item.label];
      setVpMentionPopover({ ...prev, level: nextLevel, path: nextPath, category: item.category, asset: item.asset, searchText: '' });
      vpMentionIndexRef.current = 0;
      setVpMenuLoading(true);

      try {
        let items = [];
        if (nextLevel === 'source') {
          items = [
            { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
            { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
            { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
            { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
            { id: 'imageAgent', icon: Palette, label: '图片Agent', action: 'navigate', nextLevel: 'assets', category: 'imageAgent' },
          ];
        } else if (nextLevel === 'assets') {
          const cat = item.category;
          if (cat === 'character') {
            const data = await authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}`);
            items = (data || []).filter(c => c.imageUrl).map(c => ({
              id: c.id, label: c.name, imageUrl: c.imageUrl, url: c.imageUrl,
              sourceType: 'characters',
              sourceAssetId: c.id,
              guaVideoAssetId: c.guaVideoAssetId,
              guaVideoAssetStatus: c.guaVideoAssetStatus,
              engGuaVideoAssetId: c.engGuaVideoAssetId,
              engGuaVideoAssetStatus: c.engGuaVideoAssetStatus,
              guaOminiAssetId: c.guaOminiAssetId,
              guaOminiAssetStatus: c.guaOminiAssetStatus,
              dreaminaProxyAssetId: c.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: c.dreaminaProxyAssetStatus,
              voiceSampleUrl: c.voiceSampleUrl,
              voiceSampleName: c.voiceSampleName,
              voiceBoundAt: c.voiceBoundAt,
              action: (c.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: c,
            }));
          } else if (cat === 'scene') {
            const data = await authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}`);
            items = (data || []).filter(s => s.imageUrl).map(s => ({
              id: s.id, label: s.name, imageUrl: s.imageUrl, url: s.imageUrl,
              sourceType: 'scenes',
              sourceAssetId: s.id,
              guaVideoAssetId: s.guaVideoAssetId,
              guaVideoAssetStatus: s.guaVideoAssetStatus,
              engGuaVideoAssetId: s.engGuaVideoAssetId,
              engGuaVideoAssetStatus: s.engGuaVideoAssetStatus,
              guaOminiAssetId: s.guaOminiAssetId,
              guaOminiAssetStatus: s.guaOminiAssetStatus,
              dreaminaProxyAssetId: s.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: s.dreaminaProxyAssetStatus,
              action: (s.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: s,
            }));
          } else if (cat === 'prop') {
            const data = await authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}`);
            items = (data || []).filter(p => p.imageUrl).map(p => ({
              id: p.id, label: p.name, imageUrl: p.imageUrl, url: p.imageUrl,
              sourceType: 'props',
              sourceAssetId: p.id,
              guaVideoAssetId: p.guaVideoAssetId,
              guaVideoAssetStatus: p.guaVideoAssetStatus,
              engGuaVideoAssetId: p.engGuaVideoAssetId,
              engGuaVideoAssetStatus: p.engGuaVideoAssetStatus,
              guaOminiAssetId: p.guaOminiAssetId,
              guaOminiAssetStatus: p.guaOminiAssetStatus,
              dreaminaProxyAssetId: p.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: p.dreaminaProxyAssetStatus,
              action: (p.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: p,
            }));
          } else if (cat === 'storyboard') {
            const data = await authenticatedFetch(`${API_HOST}/api/storyboard/load?episodeId=${episodeId}`);
            items = (data || []).filter(s => s.generated_image).map((s, i) => ({
              id: `shot-${i}`, label: `镜头${s.shot_number || i + 1}`, imageUrl: s.generated_image, url: s.generated_image,
              action: 'select',
            }));
          } else if (cat === 'imageAgent') {
            const data = await authenticatedFetch(`${API_HOST}/api/resource/query`, {
              method: 'POST', body: JSON.stringify({ projectId, type: 'IMAGE', page: 0, size: 50 }),
            });
            const list = data?.content || data || [];
            items = list.filter(r => r.url).map(r => ({
              id: r.id, label: r.prompt || r.name || '图片', imageUrl: r.url, url: r.url,
              action: 'select',
            }));
          }
        } else if (nextLevel === 'variants') {
          const asset = item.asset;
          const variantCategory = item.category || prev.category || '';
          const assetName = asset.name || '主体';
          if (asset) {
            items = [{
              id: 'main', label: assetName, imageUrl: asset.imageUrl, url: asset.imageUrl,
              action: 'select', category: variantCategory,
              sourceType: variantCategory === 'character' ? 'characters' : variantCategory === 'scene' ? 'scenes' : variantCategory === 'prop' ? 'props' : variantCategory,
              sourceAssetId: asset.id,
              guaVideoAssetId: asset.guaVideoAssetId,
              guaVideoAssetStatus: asset.guaVideoAssetStatus,
              engGuaVideoAssetId: asset.engGuaVideoAssetId,
              engGuaVideoAssetStatus: asset.engGuaVideoAssetStatus,
              guaOminiAssetId: asset.guaOminiAssetId,
              guaOminiAssetStatus: asset.guaOminiAssetStatus,
              dreaminaProxyAssetId: asset.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: asset.dreaminaProxyAssetStatus,
              voiceSampleUrl: asset.voiceSampleUrl,
              voiceSampleName: asset.voiceSampleName,
              voiceBoundAt: asset.voiceBoundAt,
            }];
            (asset.variations || []).forEach((v, i) => {
              if (v.imageUrl) {
                items.push({
                  id: `var-${i}`, label: assetName, imageUrl: v.imageUrl, url: v.imageUrl,
                  action: 'select', category: variantCategory,
                  sourceType: variantCategory === 'character' ? 'characters' : variantCategory === 'scene' ? 'scenes' : variantCategory === 'prop' ? 'props' : variantCategory,
                  sourceAssetId: asset.id,
                  guaVideoAssetId: v.guaVideoAssetId,
                  guaVideoAssetStatus: v.guaVideoAssetStatus,
                  engGuaVideoAssetId: v.engGuaVideoAssetId,
                  engGuaVideoAssetStatus: v.engGuaVideoAssetStatus,
                  guaOminiAssetId: v.guaOminiAssetId,
                  guaOminiAssetStatus: v.guaOminiAssetStatus,
                  dreaminaProxyAssetId: v.dreaminaProxyAssetId,
                  dreaminaProxyAssetStatus: v.dreaminaProxyAssetStatus,
                  voiceSampleUrl: asset.voiceSampleUrl,
                  voiceSampleName: asset.voiceSampleName,
                  voiceBoundAt: asset.voiceBoundAt,
                });
              }
            });
          }
        }
        setVpMenuItems(items);
      } catch (err) {
        console.error('加载菜单失败:', err);
        setVpMenuItems([]);
      } finally {
        setVpMenuLoading(false);
      }
    }
  }, [vpMentionPopover, projectId, episodeId]);

  // ── @ 多级菜单：选中（去重）──
  const handleVpMenuSelect = useCallback((item) => {
    if (!item.url) return;

    // select-existing：已有参考图，只插入 chip，不添加到参考图列表
    if (item.action === 'select-existing') {
      handleVpMentionSelect({ id: item.id, name: item.label, url: item.url, category: vpMentionPopover?.category || item.category || '' });
      return;
    }

    // 新参考图：去重添加 + 记录名字
    if (isGuaVideo) {
      const currentRefs = scene.video_gua_ref_images || [];
      if (!currentRefs.includes(item.url)) {
        handleChange(index, 'video_gua_ref_images', [...currentRefs, item.url]);
        handleChange(index, 'video_gua_ref_items', [
          ...(scene.video_gua_ref_items || []).filter((ref) => (ref.url || ref.imageUrl || ref.base64) !== item.url),
          {
            url: item.url,
            name: item.label || item.name,
            type: item.category,
            sourceType: item.sourceType,
            sourceAssetId: item.sourceAssetId,
            guaVideoAssetId: item.guaVideoAssetId,
            guaVideoAssetStatus: item.guaVideoAssetStatus,
            engGuaVideoAssetId: item.engGuaVideoAssetId,
            engGuaVideoAssetStatus: item.engGuaVideoAssetStatus,
            guaOminiAssetId: item.guaOminiAssetId,
            guaOminiAssetStatus: item.guaOminiAssetStatus,
            dreaminaProxyAssetId: item.dreaminaProxyAssetId,
            dreaminaProxyAssetStatus: item.dreaminaProxyAssetStatus,
            voiceSampleUrl: item.voiceSampleUrl,
            voiceSampleName: item.voiceSampleName,
            voiceBoundAt: item.voiceBoundAt,
          },
        ]);
        setVpRefNames(prev => new Map(prev).set(item.url, item.label));
      }
    } else if (isWanx && activeWanxMode === "reference_to_video") {
      const mediaType = inferWanxVideoReferenceType(item, item.url);
      const added = addWanxReferenceMedia(createWanxVideoMediaItem({
        type: mediaType,
        url: item.url,
        name: item.label || item.name,
        duration: item.durationSeconds ?? item.duration,
      }));
      if (!added) return;
    }
    handleVpMentionSelect({ id: item.id, name: item.label, url: item.url, category: vpMentionPopover?.category || item.category || '' });
  }, [activeWanxMode, addWanxReferenceMedia, handleChange, handleVpMentionSelect, index, isGuaVideo, isWanx, scene.video_gua_ref_images, scene.video_gua_ref_items, vpMentionPopover]);

  // ── @ 多级菜单：本地上传 ──
  const handleVpMentionUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isWanx && file.size > 30 * 1024 * 1024) {
      toast.warning('图片不能超过30MB');
      e.target.value = '';
      return;
    }
    try {
      const wanxMetadata = isWanx
        ? await validateWanxVideoMediaFile(file, WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE)
        : null;
      const url = await uploadFileToCos(file);
      const name = file.name.split('.')[0] || '本地图片';
      if (isGuaVideo) {
        const currentRefs = scene.video_gua_ref_images || [];
        if (!currentRefs.includes(url)) {
          handleChange(index, 'video_gua_ref_images', [...currentRefs, url]);
          setVpRefNames(prev => new Map(prev).set(url, name));
        }
      } else if (isWanx) {
        const added = addWanxReferenceMedia(createWanxVideoMediaItem({
          type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
          url,
          name: file.name,
          ...wanxMetadata,
        }));
        if (!added) return;
      }
      handleVpMentionSelect({ id: Date.now(), name, url, category: isWanx ? WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE : 'upload' });
    } catch (err) {
      toast.error('上传失败: ' + err.message);
    } finally {
      e.target.value = '';
    }
  }, [addWanxReferenceMedia, handleChange, handleVpMentionSelect, index, isGuaVideo, isWanx, scene.video_gua_ref_images, toast]);

  const handleVpMentionVideoUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isWanx && file.size > 50 * 1024 * 1024) {
      toast.warning('视频不能超过50MB');
      e.target.value = '';
      return;
    }
    try {
      const wanxMetadata = isWanx
        ? await validateWanxVideoMediaFile(file, WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO)
        : null;
      const url = await uploadFileToCos(file);
      const name = file.name.split('.')[0] || '本地视频';
      if (isGuaVideo) {
        const currentRefs = scene.video_gua_ref_videos || [];
        if (!currentRefs.includes(url)) {
          handleChange(index, 'video_gua_ref_videos', [...currentRefs, url]);
          setVpRefNames(prev => new Map(prev).set(url, name));
        }
      } else if (isWanx) {
        const added = addWanxReferenceMedia(createWanxVideoMediaItem({
          type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO,
          url,
          name: file.name,
          ...wanxMetadata,
        }));
        if (!added) return;
      }
      handleVpMentionSelect({ id: Date.now(), name, url, category: isWanx ? WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO : 'video' });
    } catch (err) {
      toast.error('上传失败: ' + err.message);
    } finally {
      e.target.value = '';
    }
  }, [addWanxReferenceMedia, handleChange, handleVpMentionSelect, index, isGuaVideo, isWanx, scene.video_gua_ref_videos, toast]);

  // ── @ 多级菜单：返回上一级 ──
  const handleVpMenuBack = useCallback(() => {
    if (!vpMentionPopover || vpMentionPopover.path.length === 0) return;
    const newPath = [...vpMentionPopover.path];
    newPath.pop();
    if (newPath.length === 0) {
      setVpMentionPopover({ ...vpMentionPopover, level: 'root', path: [], searchText: '' });
      setVpMenuItems([]);
    } else {
      setVpMentionPopover({ ...vpMentionPopover, level: 'source', path: ['资源管理器'], searchText: '' });
      setVpMenuItems([
        { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
        { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
        { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
        { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
        { id: 'imageAgent', icon: Palette, label: '图片Agent', action: 'navigate', nextLevel: 'assets', category: 'imageAgent' },
      ]);
    }
    vpMentionIndexRef.current = 0;
  }, [vpMentionPopover]);

  // 键盘
  const handleVpEditorKeyDown = useCallback((e) => {
    const filtered = vpFilteredRef.current;
    if (vpMentionPopover && filtered.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); updateVpMentionIndex((vpMentionIndexRef.current + 1) % filtered.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); updateVpMentionIndex((vpMentionIndexRef.current - 1 + filtered.length) % filtered.length); return; }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[Math.min(vpMentionIndexRef.current, filtered.length - 1)];
        if (!item) return;
        if (item.action === 'select' || item.action === 'select-existing') handleVpMenuSelect(item);
        else if (item.action === 'navigate' || item.action === 'upload' || item.action === 'upload-image' || item.action === 'upload-video') handleVpMenuNavigate(item);
        else handleVpMentionSelect(item);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (vpMentionPopover.path?.length > 0) handleVpMenuBack();
        else setVpMentionPopover(null);
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); setVpMentionPopover(null); return; }
    }
    if (e.key === 'Backspace') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && sel.isCollapsed) {
        const r = sel.getRangeAt(0);
        const node = r.startContainer;
        if (node.nodeType === Node.TEXT_NODE && r.startOffset === 0) {
          const prev = node.previousSibling;
          if (prev?.dataset?.mention) { e.preventDefault(); prev.remove(); handleChange(index, "video_prompt", getVpSerialized()); return; }
        }
        if (node === videoPromptEditorRef.current) {
          const child = node.childNodes[r.startOffset - 1];
          if (child?.dataset?.mention) { e.preventDefault(); child.remove(); handleChange(index, "video_prompt", getVpSerialized()); return; }
        }
      }
    }
  }, [vpMentionPopover, updateVpMentionIndex, handleVpMentionSelect, getVpSerialized, index, handleChange]);

  // 初始化 editor 内容 + 监听外部变化同步
  useEffect(() => {
    if (!videoPromptEditorRef.current) return;
    if (vpEditingRef.current) {
      vpEditingRef.current = false;
      return;
    }
    const currentText = getVpSerialized();
    const externalText = scene.video_prompt || '';
    if (currentText !== externalText) {
      renderPromptToEditor(videoPromptEditorRef.current, externalText, 'var(--color-purple-primary)');
    }
  }, [scene.video_prompt]);

  // GuaVideo ref2v: 自动填入用户已手动选择的资产引用图（仅当 video_gua_ref_images 为空时）
  useEffect(() => {
    if (!isGuaVideo || activeGuaMode !== "ref2v") return;
    if (scene.video_gua_ref_images?.length > 0) return;

    const autoImages = [];
    // 资产引用图
    const collectRefs = (ids, options, variantIndexes) => {
      if (!ids?.length || !options?.length) return;
      ids.forEach((id, i) => {
        const item = options.find(opt => opt.id === id);
        if (!item) return;
        const vi = variantIndexes?.[i] ?? -1;
        let url = item.imageUrl;
        if (vi >= 0 && item.variations?.[vi]?.imageUrl) url = item.variations[vi].imageUrl;
        if (url && !autoImages.includes(url)) autoImages.push(url);
      });
    };
    collectRefs(scene.refCharacterIds, characterOptions, scene.refCharacterVariantIndexes);
    collectRefs(scene.refSceneIds, sceneOptions, scene.refSceneVariantIndexes);
    collectRefs(scene.refPropIds, propOptions, scene.refPropVariantIndexes);

    if (autoImages.length > 0) {
      handleChange(index, "video_gua_ref_images", autoImages.slice(0, 9));
    }
  }, [
    isGuaVideo,
    activeGuaMode,
    scene.video_gua_ref_images,
    scene.refCharacterIds,
    scene.refCharacterVariantIndexes,
    scene.refSceneIds,
    scene.refSceneVariantIndexes,
    scene.refPropIds,
    scene.refPropVariantIndexes,
    characterOptions,
    sceneOptions,
    propOptions,
    handleChange,
    index,
  ]);

  const defaultViduSubModel = "viduq3-turbo";
  const currentSubModelId = scene.video_sub_model || defaultViduSubModel;
  const subModelConfig = VIDU_SUB_MODELS.find(m => m.id === currentSubModelId) || VIDU_SUB_MODELS[0];

  const currentWangsuSubModelId = scene.video_sub_model || WANGSUSORA_SUB_MODELS[0]?.id;
  const wangsuSubModelConfig = WANGSUSORA_SUB_MODELS.find(m => m.id === currentWangsuSubModelId) || WANGSUSORA_SUB_MODELS[0];

  const defaultKlingSubModel = "kling-v3";
  const currentKlingSubModelId = isKling ? (scene.video_sub_model || defaultKlingSubModel) : defaultKlingSubModel;
  const klingSubModelConfig = KLING_SUB_MODELS.find(m => m.id === currentKlingSubModelId) || KLING_SUB_MODELS[0];
  const klingDuration = scene.video_kling_duration ?? klingSubModelConfig?.defaultDuration ?? 5;
  const [klingMin, klingMax] = klingSubModelConfig?.durationRange || [3, 15];
  const klingMode = scene.video_kling_mode ?? klingSubModelConfig?.defaultMode ?? "pro";
  const klingSound = scene.video_kling_sound ?? "on";

  const viduDuration = scene.video_vidu_duration ?? subModelConfig?.defaultDuration ?? 5;
  const [min, max] = subModelConfig?.durationRange || [1, 16];

  const wangsuDuration = scene.video_wangsu_duration ?? wangsuSubModelConfig?.defaultDuration ?? 4;
  const [wangsuMin, wangsuMax] = wangsuSubModelConfig?.durationRange || [4, 12];

  const getResolutions = () => {
    if (isWangsuSora) return wangsuSubModelConfig?.resolutions || [];
    if (!subModelConfig) return [];
    if (subModelConfig.resolutionByDuration) return subModelConfig.resolutionByDuration[viduDuration] || subModelConfig.resolutions;
    return subModelConfig.resolutions;
  };
  const availableResolutions = getResolutions();

  const defaultResolution = isVidu ? "720p" : (isWangsuSora ? wangsuSubModelConfig?.defaultResolution : subModelConfig?.defaultResolution);
  const currentResolution = availableResolutions.includes(scene.video_resolution) ? scene.video_resolution : defaultResolution;

  const bgmDisabled = subModelConfig ? !subModelConfig.supportsBgm || (subModelConfig.id.startsWith("viduq2") && viduDuration >= 9) : true;

  const handleSubModelChange = (subModelId) => {
    const sm = VIDU_SUB_MODELS.find(m => m.id === subModelId);
    if (!sm) return;
    handleChange(index, "video_sub_model", subModelId);
    const [smMin, smMax] = sm.durationRange;
    const curDur = scene.video_vidu_duration ?? sm.defaultDuration;
    if (curDur < smMin || curDur > smMax) handleChange(index, "video_vidu_duration", sm.defaultDuration);
    const newResolutions = sm.resolutionByDuration ? (sm.resolutionByDuration[scene.video_vidu_duration ?? sm.defaultDuration] || sm.resolutions) : sm.resolutions;
    if (!newResolutions.includes(scene.video_resolution)) handleChange(index, "video_resolution", sm.defaultResolution);
  };

  const handleViduDurationChange = (val) => {
    const num = Math.max(min, Math.min(max, Number(val)));
    handleChange(index, "video_vidu_duration", num);
    if (subModelConfig?.resolutionByDuration) {
      const allowed = subModelConfig.resolutionByDuration[num] || subModelConfig.resolutions;
      if (!allowed.includes(scene.video_resolution)) handleChange(index, "video_resolution", allowed[0] || subModelConfig.defaultResolution);
    }
  };

  const handleWangsuSubModelChange = (subModelId) => {
    const sm = WANGSUSORA_SUB_MODELS.find(m => m.id === subModelId);
    if (!sm) return;
    handleChange(index, "video_sub_model", subModelId);
    const [smMin, smMax] = sm.durationRange;
    const curDur = scene.video_wangsu_duration ?? sm.defaultDuration;
    if (curDur < smMin || curDur > smMax) handleChange(index, "video_wangsu_duration", sm.defaultDuration);
    if (!sm.resolutions.includes(scene.video_resolution)) handleChange(index, "video_resolution", sm.defaultResolution);
  };

  const handleWangsuDurationChange = (val) => {
    const num = Math.max(wangsuMin, Math.min(wangsuMax, Number(val)));
    handleChange(index, "video_wangsu_duration", num);
  };

  const handleKlingSubModelChange = (subModelId) => {
    const sm = KLING_SUB_MODELS.find(m => m.id === subModelId);
    if (!sm) return;
    handleChange(index, "video_sub_model", subModelId);
    const [smMin, smMax] = sm.durationRange;
    const curDur = scene.video_kling_duration ?? sm.defaultDuration;
    if (curDur < smMin || curDur > smMax) handleChange(index, "video_kling_duration", sm.defaultDuration);
  };

  const handleKlingDurationChange = (val) => {
    const num = Math.max(klingMin, Math.min(klingMax, Number(val)));
    handleChange(index, "video_kling_duration", num);
  };

  // ── 多镜头编辑逻辑 ──
  const msTotalDuration = useMemo(() => {
    const kd = scene.video_kling_duration;
    if (kd) return Number(kd);
    return parseInt((scene.time || '10s').replace('s', ''), 10) || 10;
  }, [scene.video_kling_duration, scene.time]);

  const [msShots, setMsShots] = useState(() => {
    const half = Math.floor(msTotalDuration / 2);
    return [
      { index: 1, prompt: '', duration: half },
      { index: 2, prompt: '', duration: msTotalDuration - half },
    ];
  });

  const msAllocated = useMemo(() => msShots.reduce((s, sh) => s + sh.duration, 0), [msShots]);
  const msRemaining = msTotalDuration - msAllocated;
  const msDurationValid = msRemaining === 0;

  // 首帧参考图
  const msFirstFrame = useMemo(() => {
    const img = scene.generated_image;
    return img ? { id: '__ff__', name: '首帧', imageUrl: img, tag: 'image_1', isFirst: true } : null;
  }, [scene.generated_image]);

  // 资产参考图
  const msAssetRefs = useMemo(() => {
    const refs = [];
    let idx = msFirstFrame ? 1 : 0;
    const build = (ids, opts, vis) => (ids || []).forEach((id, i) => {
      const item = opts?.find(o => o.id === id);
      if (!item) return;
      const vi = vis?.[i] ?? -1;
      let url = item.imageUrl;
      if (vi >= 0 && item.variations?.[vi]?.imageUrl) url = item.variations[vi].imageUrl;
      if (url) { idx++; refs.push({ name: item.name, imageUrl: url, tag: `image_${idx}` }); }
    });
    build(scene.refCharacterIds, characterOptions, scene.refCharacterVariantIndexes);
    build(scene.refSceneIds, sceneOptions, scene.refSceneVariantIndexes);
    build(scene.refPropIds, propOptions, scene.refPropVariantIndexes);
    return refs;
  }, [scene, characterOptions, sceneOptions, propOptions, msFirstFrame]);

  const msAllRefs = useMemo(() => msFirstFrame ? [msFirstFrame, ...msAssetRefs] : msAssetRefs, [msFirstFrame, msAssetRefs]);

  const handleMsAddShot = useCallback(() => {
    if (msShots.length >= 6) return;
    setMsShots(prev => [...prev, { index: prev.length + 1, prompt: '', duration: 3 }]);
  }, [msShots.length]);

  const handleMsRemoveShot = useCallback((i) => {
    if (msShots.length <= 2) return;
    setMsShots(prev => prev.filter((_, j) => j !== i).map((s, j) => ({ ...s, index: j + 1 })));
  }, [msShots.length]);

  const handleMsUpdatePrompt = useCallback((i, prompt) => {
    setMsShots(prev => { const n = [...prev]; n[i] = { ...n[i], prompt }; return n; });
  }, []);

  const handleMsUpdateDuration = useCallback((i, dur) => {
    const d = Math.max(1, Math.min(15, parseInt(dur, 10) || 1));
    setMsShots(prev => { const n = [...prev]; n[i] = { ...n[i], duration: d }; return n; });
  }, []);

  const handleMsRefClick = useCallback((ref) => {
    if (focusedShotIdx == null) return;
    const ta = msTextareaRefs.current[focusedShotIdx];
    if (!ta) return;
    const tag = `<<<${ref.tag}>>>`;
    const start = ta.selectionStart;
    const cur = msShots[focusedShotIdx]?.prompt || '';
    handleMsUpdatePrompt(focusedShotIdx, cur.substring(0, start) + tag + cur.substring(ta.selectionEnd));
    requestAnimationFrame(() => { ta.focus(); const p = start + tag.length; ta.setSelectionRange(p, p); });
  }, [focusedShotIdx, msShots, handleMsUpdatePrompt]);

  const handleMsGenerate = useCallback(() => {
    if (!msDurationValid || !handleGenerateVideo) return;
    const multiPrompt = msShots.map((sh, i) => ({
      index: i + 1,
      prompt: sh.prompt || scene.video_prompt || scene.visual_content || '',
      duration: String(sh.duration),
    }));
    handleGenerateVideo(index, {
      multiShot: true,
      shotType: 'customize',
      multiPrompt,
      refCharacterIds: scene.refCharacterIds || [],
      refSceneIds: scene.refSceneIds || [],
      refPropIds: scene.refPropIds || [],
      refCharacterVariantIndexes: scene.refCharacterVariantIndexes,
      refSceneVariantIndexes: scene.refSceneVariantIndexes,
      refPropVariantIndexes: scene.refPropVariantIndexes,
    });
    onClose();
  }, [msDurationValid, handleGenerateVideo, msShots, scene, index, onClose]);

  // ── JSX: 侧滑抽屉 ──
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200]">
      {/* 半透明遮罩（不可点击关闭） */}
      <div className={`absolute inset-0 bg-[var(--overlay-light)] backdrop-blur-sm ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`} />

      {/* 抽屉面板 - 从右侧滑入 */}
      <div
        ref={panelRef}
        className={`absolute top-0 right-0 h-full w-[520px] max-w-[90vw] flex flex-col vsp-drawer ${exiting ? "animate-out slide-out-to-right-4 duration-150" : "animate-in slide-in-from-right duration-200"}`}
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderLeft: '1px solid var(--color-border-primary)',
          boxShadow: 'var(--shadow-modal)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 统一 hover 效果 */}
        <style>{`
          .vsp-drawer button,
          .vsp-drawer label,
          .vsp-drawer [data-clickable] {
            transition: all 0.2s ease;
          }
          .vsp-drawer button:not(:disabled):hover,
          .vsp-drawer label:hover,
          .vsp-drawer [data-clickable]:hover {
            filter: brightness(1.12);
            box-shadow: var(--shadow-card);
          }
          .vsp-drawer button:not(:disabled):active,
          .vsp-drawer [data-clickable]:active {
            transform: scale(0.97);
            filter: brightness(0.95);
          }
          .vsp-drawer textarea:focus {
            border-color: var(--color-purple-primary) !important;
            box-shadow: 0 0 0 2px var(--color-purple-light);
          }
          .vsp-drawer input[type="range"] {
            transition: all 0.15s ease;
          }
          .vsp-drawer input[type="range"]:hover {
            filter: brightness(1.2);
          }
          .vsp-drawer input[type="number"]:focus {
            border-color: var(--color-purple-primary) !important;
            box-shadow: 0 0 0 2px var(--color-purple-light);
          }
          .vsp-drawer .model-card {
            transition: all 0.2s ease;
          }
          .vsp-drawer .model-card:hover {
            transform: translateY(-1px);
            box-shadow: var(--shadow-card);
          }
          .vsp-drawer .model-card:active {
            transform: translateY(0) scale(0.98);
          }
          .vsp-drawer .ref-img {
            transition: all 0.2s ease;
          }
          .vsp-drawer .ref-img:hover {
            transform: scale(1.05);
            box-shadow: var(--shadow-card);
            z-index: 10;
          }
        `}</style>
        {/* ── Header ── */}
        <div className="shrink-0" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>视频设置</h3>
              <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>#{scene.shot_number}</span>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:scale-110"
              style={{ backgroundColor: 'var(--color-error)', color: 'white' }}
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
          {/* 模型 Tab 栏 */}
          <div className="flex px-3 gap-0.5 overflow-x-auto custom-scrollbar pb-0">
            {visibleVideoModels.map((model) => {
              const isSel = selectedModelId === model.id;
              const color = model.accentColor || theme.colors.bluePrimary;
              return (
                <button
                  key={model.id}
                  onClick={() => {
                    handleChange(index, "video_provider", model.id);
                    handleChange(index, "video_model", model.id);
                    const persistedWanxSubModel = scene.video_sub_model || scene.videoSubModel;
                    if (model.id === VIDEO_PROVIDER_WANX && !isWanxVideoModel(persistedWanxSubModel)) {
                      handleChange(index, "video_sub_model", WANX_VIDEO_MODEL);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 text-2xs font-bold whitespace-nowrap shrink-0 transition-all relative"
                  style={{
                    color: isSel ? color : 'var(--color-text-muted)',
                    borderBottom: isSel ? `2px solid ${color}` : '2px solid transparent',
                    backgroundColor: isSel ? `${color}08` : 'transparent',
                    borderRadius: '8px 8px 0 0',
                  }}
                >
                  <model.icon size={12} />
                  {model.name.split(" ")[0]}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── 滚动内容区 ── */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-5">

          {/* ═══ 参考图 — 非 GuaVideo 或 GuaVideo 非 ref2v ═══ */}
          {!isGuaVideo && (!isWanx || activeWanxMode === "reference_to_video") && (
            <div className="space-y-2">
              <SectionLabel><ImageIcon size={13} /> 参考图</SectionLabel>
              <RefImageSection
                scene={scene}
                characterOptions={characterOptions}
                sceneOptions={sceneOptions}
                propOptions={propOptions}
                preferredProvider={projectGuaModelId}
              />
            </div>
          )}

          {/* ═══ GuaVideo ref2v 参考图 — 扁平列表 ═══ */}
          {isGuaVideo && activeGuaMode === "ref2v" && (() => {
            const refImages = scene.video_gua_ref_images || [];
            const total = refImages.length;
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <SectionLabel><ImageIcon size={13} /> 参考图</SectionLabel>
                  <span className="text-2xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: total >= 9 ? `${theme.colors.error}20` : 'color-mix(in srgb, var(--color-purple-primary) 13%, transparent)', color: total >= 9 ? theme.colors.error : 'var(--color-purple-primary)' }}>{total}/9</span>
                </div>
                <div className="flex flex-wrap gap-3 p-1">
                  {refImages.map((url, i) => (
                    <div key={i} className="ref-img relative group w-16 h-16 rounded-lg border-2"
                      style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-overlay)' }}
                      onMouseEnter={(e) => handleFrameHover(e, url)}
                      onMouseLeave={() => setHoveredFrameImage(null)}
                    >
                      <img src={url} className="w-full h-full object-cover rounded-md" alt={`参考图${i + 1}`} />
                      <button
                        onClick={() => {
                          const delUrl = refImages[i];
                          const newList = [...refImages];
                          newList.splice(i, 1);
                          handleChange(index, "video_gua_ref_images", newList);
                          handleChange(index, "video_gua_ref_items", (scene.video_gua_ref_items || []).filter((ref) => (ref.url || ref.imageUrl || ref.base64) !== delUrl));
                          setVpRefNames(prev => { const m = new Map(prev); m.delete(delUrl); return m; });
                        }}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        style={{ backgroundColor: theme.colors.error, color: 'white' }}
                      >
                        <X size={8} strokeWidth={3} />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 text-center py-px text-2xs font-bold truncate px-0.5" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>{vpRefNames.get(url) || (i + 1)}</div>
                    </div>
                  ))}
                  {total < 9 && (
                    <div className="relative w-16 h-16 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all hover:border-solid group"
                      style={{ borderColor: '#8b5cf650', backgroundColor: '#8b5cf608' }}
                      onClick={() => setShowGuaResourceSelector(true)}
                    >
                      <Plus size={16} style={{ color: 'var(--color-purple-primary)' }} />
                      <span className="text-2xs font-bold mt-0.5" style={{ color: 'var(--color-purple-primary)' }}>添加</span>
                      {guaImageUploading && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--overlay-medium)' }}>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {total < 9 && (
                  <div className="flex gap-2">
                    <label className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg cursor-pointer transition-all text-2xs font-medium hover:bg-[var(--color-bg-tertiary)]"
                      style={{ border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                    >
                      <Upload size={11} /> 本地上传
                      <input ref={guaImageUploadRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 30 * 1024 * 1024) { (await appAlert("图片不能超过30MB")); return; }
                        setGuaImageUploading(true);
                        try {
                          const url = await uploadFileToCos(file);
                          const name = file.name.split('.')[0] || '本地图片';
                          const newRefs = [...refImages, url];
                          handleChange(index, "video_gua_ref_images", newRefs);
                          appendChipToEditor(name, url, 'upload');
                          setVpRefNames(prev => new Map(prev).set(url, name));
                        } catch (err) { (await appAlert("上传失败: " + err.message)); }
                        setGuaImageUploading(false);
                        e.target.value = '';
                      }} />
                    </label>
                    <button onClick={() => setShowGuaResourceSelector(true)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-2xs font-medium transition-all hover:bg-[var(--color-bg-tertiary)]"
                      style={{ border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                    >
                      <FolderOpen size={11} /> 资源库
                    </button>
                  </div>
                )}
                <div className="text-2xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                  支持 1~9 张参考图。资产引用图、首帧图可通过“资源库”或“本地上传”添加，图片以 URL 传递。
                </div>
              </div>
            );
          })()}

          {/* GuaVideo 参考图资源选择器 */}
          {showGuaResourceSelector && (
            <ResourceSelector
              isOpen={showGuaResourceSelector}
              onClose={() => setShowGuaResourceSelector(false)}
              onSelect={async (resource) => {
                const url = resource.url || resource.thumbnailUrl;
                if (!url) return;
                const cur = scene.video_gua_ref_images || [];
                if (cur.length >= 9) { (await appAlert("最多9张参考图")); return; }
                const name = resource.name || `图片${cur.length + 1}`;
                const newRefs = [...cur, url];
                handleChange(index, "video_gua_ref_images", newRefs);
                handleChange(index, "video_gua_ref_items", [
                  ...(scene.video_gua_ref_items || []).filter((ref) => (ref.url || ref.imageUrl || ref.base64) !== url),
                  {
                    url,
                    name,
                    type: resource.typeCode || resource.type || resource.source,
                    source: resource.source,
                    sourceType: resource.source,
                    sourceAssetId: resource.assetId,
                    guaVideoAssetId: resource.guaVideoAssetId,
                    guaVideoAssetStatus: resource.guaVideoAssetStatus,
                    engGuaVideoAssetId: resource.engGuaVideoAssetId,
                    engGuaVideoAssetStatus: resource.engGuaVideoAssetStatus,
                    guaOminiAssetId: resource.guaOminiAssetId,
                    guaOminiAssetStatus: resource.guaOminiAssetStatus,
                    dreaminaProxyAssetId: resource.dreaminaProxyAssetId,
                    dreaminaProxyAssetStatus: resource.dreaminaProxyAssetStatus,
                    voiceSampleUrl: resource.voiceSampleUrl,
                    voiceSampleName: resource.voiceSampleName,
                    voiceBoundAt: resource.voiceBoundAt,
                  },
                ]);
                appendChipToEditor(name, url);
                setVpRefNames(prev => new Map(prev).set(url, name));
              }}
              projectId={projectId}
              type="all"
              title="选择参考图"
            />
          )}

          {/* ═══ 视频提示词 ═══ */}
          <div className="space-y-1.5">
            <SectionLabel><Film size={13} /> 视频提示词</SectionLabel>
            <div className="relative">
              <div
                ref={videoPromptEditorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleVpEditorInput}
                onKeyDown={handleVpEditorKeyDown}
                onPaste={(e) => { const html = e.clipboardData.getData('text/html'); if (html && html.includes('data-mention')) return; e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
                data-placeholder="输入视频提示词...输入 @ 引用参考图"
                className="w-full rounded-xl px-3 py-2.5 text-xs leading-relaxed outline-none min-h-[200px] max-h-[400px] overflow-y-auto whitespace-pre-wrap break-words"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: '1px solid var(--color-border-primary)',
                  color: 'var(--color-text-primary)',
                }}
              />
              {vpMenuAnim.mounted && (vpMentionPopover || lastVpPopoverRef.current) && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[500]" onMouseDown={() => setVpMentionPopover(null)}>
                  <div
                    className={`fixed w-56 rounded-2xl shadow-2xl py-2 px-1 max-h-64 overflow-y-auto ${vpMenuAnim.exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-150"}`}
                    style={{
                      ...((() => {
                        const sel = window.getSelection();
                        let cursorRect = null;
                        if (sel?.rangeCount > 0) {
                          const range = sel.getRangeAt(0);
                          const rects = range.getClientRects();
                          cursorRect = rects.length > 0 ? rects[0] : range.getBoundingClientRect();
                        }
                        const fallback = videoPromptEditorRef.current?.getBoundingClientRect();
                        const bottom = cursorRect?.bottom || fallback?.bottom || 0;
                        const top = cursorRect?.top || fallback?.top || 0;
                        let left = cursorRect?.left || fallback?.left || 0;
                        const menuW = 228;
                        if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
                        if (left < 8) left = 8;
                        if (top < 300) {
                          return { top: bottom + 6, left };
                        }
                        return { bottom: window.innerHeight - top + 6, left };
                      })()),
                      backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 92%, transparent)',
                      backdropFilter: 'blur(24px) saturate(1.6)',
                      WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
                      border: '1px solid var(--color-border-primary)',
                    }}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    {(vpMentionPopover || lastVpPopoverRef.current)?.path?.length > 0 && (
                      <div className="flex items-center gap-1 px-3 py-1.5 mb-1" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                        <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleVpMenuBack(); }} className="text-2xs hover:underline" style={{ color: 'var(--color-text-muted)' }}>←</button>
                        {(vpMentionPopover || lastVpPopoverRef.current).path.map((p, i) => (
                          <span key={i} className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{i > 0 && ' › '}{p}</span>
                        ))}
                      </div>
                    )}

                    {vpMenuLoading && (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-2xs animate-pulse" style={{ color: 'var(--color-text-muted)' }}>加载中...</span>
                      </div>
                    )}

                    {!vpMenuLoading && vpVisibleMentions.map((item, i) => (
                      <button
                        key={item.id}
                        ref={el => vpMentionItemRefs.current[i] = el}
                        data-image-url={item.imageUrl || ''}
                        onMouseDown={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setAtPreview(null);
                          if (item.action === 'select' || item.action === 'select-existing') handleVpMenuSelect(item);
                          else handleVpMenuNavigate(item);
                        }}
                        onMouseEnter={() => updateVpMentionIndex(i)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-left transition-all"
                        style={{
                          color: i === 0 && vpMentionIndexRef.current === 0 ? 'var(--color-purple-primary)' : 'var(--color-text-secondary)',
                          backgroundColor: i === 0 && vpMentionIndexRef.current === 0 ? '#8b5cf618' : 'transparent',
                        }}
                      >
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} />
                        ) : item.icon ? (
                          <span className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ color: 'var(--color-text-muted)' }}>
                            {(() => { const MenuIcon = item.icon; return <MenuIcon size={15} />; })()}
                          </span>
                        ) : null}
                        <span className="truncate flex-1">{item.label || item.name}</span>
                        {item.action === 'navigate' && <span style={{ color: 'var(--color-text-muted)' }}>›</span>}
                      </button>
                    ))}
                    {!vpMenuLoading && vpHiddenMentionCount > 0 && (
                      <div className="px-3 py-2 text-center text-2xs" style={{ color: 'var(--color-text-muted)', borderTop: '1px solid var(--color-border-secondary)' }}>
                        还有 {vpHiddenMentionCount} 项，继续输入关键词缩小范围
                      </div>
                    )}

                    {!vpMenuLoading && vpFilteredMentions.length === 0 && (
                      <div className="px-3 py-4 text-center text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无内容</div>
                    )}
                  </div>

                  {/* 选中项大图预览 */}
                  {atPreview && (
                    <div className="fixed pointer-events-none" style={{
                      right: window.innerWidth - (videoPromptEditorRef.current?.getBoundingClientRect().left || 0) + 16,
                      bottom: window.innerHeight - (videoPromptEditorRef.current?.getBoundingClientRect().top || 0) + 6,
                      zIndex: 501,
                    }}>
                      <img src={atPreview.url} alt="" className="rounded-xl shadow-2xl" style={{
                        maxWidth: 'min(400px, 30vw)', maxHeight: 'min(400px, 40vh)', width: 'auto', height: 'auto', objectFit: 'contain',
                        backgroundColor: 'color-mix(in srgb, var(--color-bg-overlay) 95%, transparent)',
                        border: '1px solid var(--color-border-primary)',
                        padding: '4px',
                      }} />
                    </div>
                  )}
                </div>,
                document.body
              )}

              {/* @ 本地上传隐藏 input */}
              <input type="file" ref={vpMentionUploadRef} className="hidden" accept={isWanx ? WANX_VIDEO_ACCEPT.image : "image/*"} onChange={handleVpMentionUpload} />
              <input type="file" ref={vpMentionVideoUploadRef} className="hidden" accept={isWanx ? WANX_VIDEO_ACCEPT.video : "video/mp4,video/quicktime,video/webm"} onChange={handleVpMentionVideoUpload} />
            </div>
            <style>{`
              [data-placeholder]:empty:before { content: attr(data-placeholder); color: var(--color-text-muted); pointer-events: none; }
            `}</style>
            <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              与列表视图中的视频提示词实时同步
            </div>
          </div>

          {/* ═══ Vidu 专属参数 ═══ */}
          {isVidu && (
            <div className="space-y-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)' }}>
              <div className="flex items-center gap-2 pb-2" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                <span className="text-xs font-bold" style={{ color: theme.colors.bluePrimary }}>Vidu 参数</span>
              </div>

              {/* 子模型 */}
              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>子模型</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {VIDU_SUB_MODELS.map((sm) => {
                    const isSel = currentSubModelId === sm.id;
                    return (
                      <button key={sm.id} onClick={() => handleSubModelChange(sm.id)}
                        className="py-2 px-3 rounded-lg text-xs font-medium transition-all text-left"
                        style={{
                          backgroundColor: isSel ? theme.colors.bluePrimary : 'var(--color-bg-secondary)',
                          color: isSel ? 'white' : 'var(--color-text-secondary)',
                          border: `1px solid ${isSel ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                        }}
                      >{sm.name}</button>
                    );
                  })}
                </div>
              </div>

              {/* 时长 */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>时长</span>
                  <span className="text-2xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)', color: theme.colors.bluePrimary }}>{viduDuration}s</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{min}s</span>
                  <input type="range" min={min} max={max} step={1} value={viduDuration}
                    onChange={(e) => handleViduDurationChange(e.target.value)}
                    className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                    style={{ accentColor: theme.colors.bluePrimary }}
                  />
                  <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{max}s</span>
                </div>
              </div>

              {/* 分辨率 */}
              {availableResolutions.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>分辨率</span>
                  <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    {availableResolutions.map((r) => (
                      <button key={r} onClick={() => handleChange(index, "video_resolution", r)}
                        className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                        style={{
                          backgroundColor: currentResolution === r ? theme.colors.bluePrimary : 'transparent',
                          color: currentResolution === r ? 'white' : 'var(--color-text-secondary)',
                        }}
                      >{r}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* 生成设置 */}
              <div className="space-y-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    参考图生视频 <span className="text-2xs opacity-60">(角色一致性)</span>
                  </span>
                  <Toggle value={scene.video_vidu_use_refs ?? false} onChange={(v) => handleChange(index, "video_vidu_use_refs", v)} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>音频</span>
                  <Toggle value={scene.video_audio ?? true} onChange={(v) => handleChange(index, "video_audio", v)} />
                </div>
                {subModelConfig?.supportsAudioType && (
                  <div className="space-y-1">
                    <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>音频类型</span>
                    <div className="flex gap-1">
                      {AUDIO_TYPE_OPTIONS.map((opt) => {
                        const isSel = (scene.video_audio_type || "all") === opt.value;
                        return (
                          <button key={opt.value} onClick={() => handleChange(index, "video_audio_type", opt.value)}
                            className="flex-1 py-1.5 text-2xs rounded-lg transition-all"
                            style={{
                              backgroundColor: isSel ? theme.colors.bluePrimary : 'var(--color-bg-primary)',
                              color: isSel ? 'white' : 'var(--color-text-muted)',
                              border: `1px solid ${isSel ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                            }}
                          >{opt.label}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: bgmDisabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)' }}>
                    BGM {bgmDisabled && <span className="text-2xs opacity-60">(不支持)</span>}
                  </span>
                  <Toggle value={!bgmDisabled && (scene.video_bgm ?? false)} onChange={(v) => handleChange(index, "video_bgm", v)} disabled={bgmDisabled} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>错峰生成</span>
                  <Toggle value={scene.video_off_peak ?? false} onChange={(v) => handleChange(index, "video_off_peak", v)} />
                </div>
              </div>

              {/* 运动幅度 */}
              {subModelConfig?.supportsMovementAmplitude && (
                <div className="space-y-1.5">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>运动幅度</span>
                  <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    {MOVEMENT_OPTIONS.map((opt) => {
                      const isSel = (scene.video_movement_amplitude || "auto") === opt.value;
                      return (
                        <button key={opt.value} onClick={() => handleChange(index, "video_movement_amplitude", opt.value)}
                          className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                          style={{
                            backgroundColor: isSel ? theme.colors.bluePrimary : 'transparent',
                            color: isSel ? 'white' : 'var(--color-text-secondary)',
                          }}
                        >{opt.label}</button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Voice ID */}
              {subModelConfig?.supportsVoiceId && (
                <div className="space-y-1.5">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>Voice ID</span>
                  <input type="text" placeholder="可选，留空使用默认" value={scene.video_voice_id || ""}
                    onChange={(e) => handleChange(index, "video_voice_id", e.target.value)}
                    className="w-full text-xs px-3 py-2 rounded-lg"
                    style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', outline: 'none' }}
                  />
                </div>
              )}

              {/* Seed */}
              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>随机种子</span>
                <input type="number" placeholder="可选，留空随机" value={scene.video_seed || ""}
                  onChange={(e) => handleChange(index, "video_seed", e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full text-xs px-3 py-2 rounded-lg"
                  style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', outline: 'none' }}
                />
              </div>
            </div>
          )}

          {/* ═══ WangsuSora 专属参数 ═══ */}
          {isWangsuSora && (
            <div className="space-y-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)' }}>
              <div className="flex items-center gap-2 pb-2" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                <span className="text-xs font-bold" style={{ color: theme.colors.bluePrimary }}>Sora 2 参数</span>
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>模型版本</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {WANGSUSORA_SUB_MODELS.map((sm) => {
                    const isSel = currentWangsuSubModelId === sm.id;
                    return (
                      <button key={sm.id} onClick={() => handleWangsuSubModelChange(sm.id)}
                        className="py-2 px-3 rounded-lg text-xs font-medium transition-all text-left"
                        style={{
                          backgroundColor: isSel ? theme.colors.bluePrimary : 'var(--color-bg-secondary)',
                          color: isSel ? 'white' : 'var(--color-text-secondary)',
                          border: `1px solid ${isSel ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                        }}
                      >{sm.name}</button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>生成时长</span>
                <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {(wangsuSubModelConfig?.durations || []).map((d) => (
                    <button key={d} onClick={() => handleChange(index, "video_wangsu_duration", d)}
                      className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                      style={{
                        backgroundColor: (scene.video_wangsu_duration || wangsuSubModelConfig?.defaultDuration) === d ? theme.colors.bluePrimary : 'transparent',
                        color: (scene.video_wangsu_duration || wangsuSubModelConfig?.defaultDuration) === d ? 'white' : 'var(--color-text-secondary)',
                      }}
                    >{d}s</button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>分辨率</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {availableResolutions.map((r) => (
                    <button key={r} onClick={() => handleChange(index, "video_resolution", r)}
                      className="py-2 text-xs rounded-lg transition-all font-medium"
                      style={{
                        backgroundColor: currentResolution === r ? theme.colors.bluePrimary : 'var(--color-bg-secondary)',
                        color: currentResolution === r ? 'white' : 'var(--color-text-secondary)',
                        border: `1px solid ${currentResolution === r ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                      }}
                    >{r}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══ Kling 专属参数 ═══ */}
          {isKling && (
            <div className="space-y-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)' }}>
              <div className="flex items-center gap-2 pb-2" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                <span className="text-xs font-bold" style={{ color: theme.colors.bluePrimary }}>可灵参数</span>
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>模型版本</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {KLING_SUB_MODELS.map((sm) => {
                    const isSel = currentKlingSubModelId === sm.id;
                    return (
                      <button key={sm.id} onClick={() => handleKlingSubModelChange(sm.id)}
                        className="py-2 px-3 rounded-lg text-xs font-medium transition-all text-left"
                        style={{
                          backgroundColor: isSel ? theme.colors.bluePrimary : 'var(--color-bg-secondary)',
                          color: isSel ? 'white' : 'var(--color-text-secondary)',
                          border: `1px solid ${isSel ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                        }}
                      >{sm.name}</button>
                    );
                  })}
                </div>
              </div>

              {/* 时长 */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>时长</span>
                  <span className="text-2xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)', color: theme.colors.bluePrimary }}>{klingDuration}s</span>
                </div>
                {klingSubModelConfig?.durations ? (
                  <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    {klingSubModelConfig.durations.map((d) => (
                      <button key={d} onClick={() => handleChange(index, "video_kling_duration", d)}
                        className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                        style={{
                          backgroundColor: klingDuration === d ? theme.colors.bluePrimary : 'transparent',
                          color: klingDuration === d ? 'white' : 'var(--color-text-secondary)',
                        }}
                      >{d}s</button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{klingMin}s</span>
                    <input type="range" min={klingMin} max={klingMax} step={1} value={klingDuration}
                      onChange={(e) => handleKlingDurationChange(e.target.value)}
                      className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: theme.colors.bluePrimary }}
                    />
                    <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{klingMax}s</span>
                  </div>
                )}
              </div>

              {/* 模式 */}
              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>模式</span>
                <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {(klingSubModelConfig?.modes || ["pro", "std"]).map((m) => (
                    <button key={m} onClick={() => handleChange(index, "video_kling_mode", m)}
                      className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                      style={{
                        backgroundColor: klingMode === m ? theme.colors.bluePrimary : 'transparent',
                        color: klingMode === m ? 'white' : 'var(--color-text-secondary)',
                      }}
                    >{m === "pro" ? "高品质 (Pro)" : "标准 (Std)"}</button>
                  ))}
                </div>
              </div>

              {/* 声音 */}
              {klingSubModelConfig?.supportsSound && (
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>声音</span>
                  <div className="flex gap-1.5">
                    {[{ value: "off", label: "关闭" }, { value: "on", label: "开启" }].map((opt) => (
                      <button key={opt.value} onClick={() => handleChange(index, "video_kling_sound", opt.value)}
                        className="px-3 py-1.5 text-xs rounded-lg transition-all font-medium"
                        style={{
                          backgroundColor: klingSound === opt.value ? theme.colors.bluePrimary : 'transparent',
                          color: klingSound === opt.value ? 'white' : 'var(--color-text-secondary)',
                        }}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* 智能分镜 */}
              {(currentKlingSubModelId === "kling-v3" || currentKlingSubModelId === "kling-v3-omni" || currentKlingSubModelId === "kling-video-o1") && (
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <div>
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>智能分镜</span>
                    <p className="text-2xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>自动切分内容实现多镜头效果</p>
                  </div>
                  <Toggle value={scene.video_kling_multi_shot ?? false} onChange={(val) => handleChange(index, "video_kling_multi_shot", val)} />
                </div>
              )}

              {/* 多镜头编辑 — 内嵌 */}
              {(currentKlingSubModelId === "kling-v3" || currentKlingSubModelId === "kling-v3-omni" || currentKlingSubModelId === "kling-video-o1") && (
                <div className="space-y-2">
                  <button onClick={() => setShowMultiShotEditor(!showMultiShotEditor)}
                    className="w-full py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: showMultiShotEditor ? theme.colors.purplePrimary : 'var(--color-bg-secondary)',
                      color: showMultiShotEditor ? 'white' : theme.colors.purplePrimary,
                      border: `1px solid ${showMultiShotEditor ? theme.colors.purplePrimary : theme.colors.purplePrimary + '40'}`,
                    }}
                  >
                    <Film size={13} /> {showMultiShotEditor ? '收起多镜头编辑' : '多镜头编辑'}
                  </button>

                  {multiShotEditorAnim.mounted && (
                    <div className={`space-y-3 p-3 rounded-xl ${multiShotEditorAnim.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`} style={{ backgroundColor: 'var(--color-bg-tertiary)', border: `1px solid ${theme.colors.purplePrimary}30` }}>
                      {/* 参考图 — 点击插入标签 */}
                      {msAllRefs.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>参考图 (点击插入)</span>
                            <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{msAllRefs.length}/7</span>
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            {msAllRefs.map((ref, i) => (
                              <div key={i}
                                onClick={() => handleMsRefClick(ref)}
                                className="flex items-center gap-1.5 px-1.5 py-1 rounded-md cursor-pointer transition-all hover:ring-1"
                                style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${ref.isFirst ? theme.colors.purplePrimary + '60' : 'var(--color-border-primary)'}`, '--tw-ring-color': theme.colors.bluePrimary }}
                                title={focusedShotIdx != null ? `插入 <<<${ref.tag}>>>` : '先点击镜头输入框'}
                              >
                                <div className="w-6 h-6 rounded overflow-hidden shrink-0" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                                  {ref.imageUrl && <img src={ref.imageUrl} className="w-full h-full object-cover" alt="" />}
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <span className="text-2xs truncate" style={{ color: 'var(--color-text-primary)' }}>{ref.name}</span>
                                  <span className="text-2xs font-mono" style={{ color: ref.isFirst ? theme.colors.purplePrimary : theme.colors.bluePrimary }}>{ref.tag}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 时长统计 */}
                      <div className="flex items-center justify-between px-2 py-1.5 rounded-lg text-2xs"
                        style={{
                          backgroundColor: msDurationValid ? 'var(--color-bg-primary)' : `${theme.colors.error}10`,
                          border: `1px solid ${msDurationValid ? 'var(--color-border-primary)' : theme.colors.error}`,
                        }}
                      >
                        <span style={{ color: 'var(--color-text-muted)' }}>总 <b style={{ color: 'var(--color-text-primary)' }}>{msTotalDuration}s</b></span>
                        <span style={{ color: 'var(--color-text-muted)' }}>已分配 <b style={{ color: 'var(--color-text-primary)' }}>{msAllocated}s</b></span>
                        <span style={{ color: msRemaining === 0 ? theme.colors.success : theme.colors.error, fontWeight: 'bold' }}>
                          {msRemaining === 0 ? '✓' : msRemaining > 0 ? `余${msRemaining}s` : `超${-msRemaining}s`}
                        </span>
                      </div>

                      {/* 镜头列表 */}
                      {msShots.map((shot, si) => (
                        <div key={shot.index} className="p-2 rounded-lg space-y-1.5"
                          style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${focusedShotIdx === si ? theme.colors.bluePrimary : 'var(--color-border-primary)'}` }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className="text-2xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: theme.colors.purplePrimary, color: 'white' }}>镜头{shot.index}</span>
                              {focusedShotIdx === si && <span className="text-2xs px-1 py-px rounded-full animate-pulse" style={{ backgroundColor: `${theme.colors.bluePrimary}20`, color: theme.colors.bluePrimary }}>编辑中</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <input type="number" min={1} max={15} value={shot.duration}
                                onChange={(e) => handleMsUpdateDuration(si, e.target.value)}
                                className="w-10 text-2xs px-1 py-0.5 rounded text-center outline-none"
                                style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                              />
                              <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>s</span>
                              {msShots.length > 2 && (
                                <button onClick={() => handleMsRemoveShot(si)} className="p-0.5 rounded transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: theme.colors.error }}>
                                  <Trash2 size={11} />
                                </button>
                              )}
                            </div>
                          </div>
                          <textarea
                            ref={(el) => { msTextareaRefs.current[si] = el; }}
                            value={shot.prompt}
                            onChange={(e) => handleMsUpdatePrompt(si, e.target.value)}
                            onFocus={() => setFocusedShotIdx(si)}
                            placeholder={`镜头${shot.index}描述... 点击参考图插入<<<image_N>>>`}
                            rows={2}
                            className="w-full text-2xs px-2 py-1.5 rounded-lg resize-none outline-none"
                            style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${focusedShotIdx === si ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`, color: 'var(--color-text-primary)' }}
                          />
                        </div>
                      ))}

                      {/* 添加镜头 + 生成 */}
                      <div className="flex gap-2">
                        {msShots.length < 6 && (
                          <button onClick={handleMsAddShot}
                            className="flex-1 py-1.5 rounded-lg text-2xs font-medium flex items-center justify-center gap-1 transition-all"
                            style={{ border: '1px dashed var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                          >
                            <Plus size={11} /> 添加镜头
                          </button>
                        )}
                        <button onClick={handleMsGenerate}
                          disabled={!msDurationValid || scene.isGeneratingVideo}
                          className="flex-1 py-1.5 rounded-lg text-2xs font-bold flex items-center justify-center gap-1 transition-all disabled:opacity-40"
                          style={{ backgroundColor: theme.colors.purplePrimary, color: 'white' }}
                        >
                          <Film size={11} /> {scene.isGeneratingVideo ? '生成中...' : '确认生成'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 水印 */}
              {(currentKlingSubModelId === "kling-v3" || currentKlingSubModelId === "kling-v3-omni" || currentKlingSubModelId === "kling-video-o1") && (
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>生成水印</span>
                  <Toggle value={scene.video_kling_watermark ?? false} onChange={(val) => handleChange(index, "video_kling_watermark", val)} />
                </div>
              )}
            </div>
          )}


          {/* ═══ GuaVideo 专属参数 ═══ */}
          {isGuaVideo && (
            <div className="space-y-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)' }}>
              <div className="flex items-center gap-2 pb-2" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                <span className="text-xs font-bold" style={{ color: 'var(--color-purple-primary)' }}>{getVideoProviderLabel(effectiveGuaModelId)} 参数</span>
              </div>

              {/* 模式切换 */}
              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>生成模式</span>
                <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {[
                    { value: "i2v", label: "首帧生视频" },
                    { value: "ref2v", label: "参考生视频" },
                    { value: "t2v", label: "文生视频" },
                  ].map((m) => {
                    const isSel = activeGuaMode === m.value;
                    return (
                      <button key={m.value} onClick={() => handleGuaModeChange(m.value)}
                        className="flex-1 py-2 text-2xs rounded-lg transition-all font-bold"
                        style={{
                          backgroundColor: isSel ? 'var(--color-purple-primary)' : 'transparent',
                          color: isSel ? 'white' : 'var(--color-text-secondary)',
                        }}
                      >{m.label}</button>
                    );
                  })}
                </div>
              </div>

              {/* 子模型 */}
              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>模型版本</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {guaSubModels.map((sm) => {
                    const defaultSubModel = isDomesticOfficialSeedance(effectiveGuaModelId)
                      ? DOMESTIC_SEEDANCE_DEFAULT_MODEL
                      : effectiveGuaModelId === "guavideo"
                      ? "doubao-seedance-2-0-260128"
                      : isSeedanceVideoProvider(effectiveGuaModelId)
                        ? "dreamina-seedance-2-0"
                        : "";
                    const storedSubModel = guaSubModels.some((item) => item.id === scene.video_sub_model)
                      ? scene.video_sub_model
                      : defaultSubModel;
                    const isSel = storedSubModel === sm.id;
                    return (
                      <button key={sm.id} onClick={() => handleChange(index, "video_sub_model", sm.id)}
                        className="py-2 px-3 rounded-lg text-xs font-medium transition-all text-left"
                        style={{
                          backgroundColor: isSel ? '#8b5cf6' : 'var(--color-bg-secondary)',
                          color: isSel ? 'white' : 'var(--color-text-secondary)',
                          border: `1px solid ${isSel ? '#8b5cf6' : 'var(--color-border-primary)'}`,
                        }}
                      >{sm.name}</button>
                    );
                  })}
                </div>
              </div>

              {/* ref2v 模式：参考视频上传 */}
              {activeGuaMode === "ref2v" && (
                <div className="space-y-2">
                  {/* 参考视频 */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>参考视频</span>
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{(scene.video_gua_ref_videos || []).length}/3</span>
                    </div>
                    <div className="space-y-1.5">
                      {(scene.video_gua_ref_videos || []).map((url, vi) => (
                        <div key={vi} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                          <Video size={14} style={{ color: 'var(--color-purple-primary)' }} />
                          <span className="text-2xs flex-1 truncate" style={{ color: 'var(--color-text-primary)' }}>{url.split('/').pop()}</span>
                          <button onClick={() => {
                            const newList = [...(scene.video_gua_ref_videos || [])];
                            newList.splice(vi, 1);
                            handleChange(index, "video_gua_ref_videos", newList);
                          }} className="p-0.5 rounded transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: theme.colors.error }}>
                            <X size={11} />
                          </button>
                        </div>
                      ))}
                      {(scene.video_gua_ref_videos || []).length < 3 && (
                        <label className="flex items-center justify-center gap-1.5 py-2 rounded-lg cursor-pointer transition-all text-2xs font-medium"
                          style={{ border: '1px dashed var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                        >
                          <Upload size={12} /> 上传视频 (mp4/mov, ≤50MB)
                          <input type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 50 * 1024 * 1024) { (await appAlert("视频文件不能超过50MB")); return; }
                            try {
                              const url = await uploadFileToCos(file);
                              handleChange(index, "video_gua_ref_videos", [...(scene.video_gua_ref_videos || []), url]);
                            } catch (err) { (await appAlert("上传失败: " + err.message)); }
                            e.target.value = '';
                          }} />
                        </label>
                      )}
                    </div>
                  </div>

                </div>
              )}

              {/* t2v 模式：联网搜索 */}
              {activeGuaMode === "t2v" && (
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <div>
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>联网搜索</span>
                    <p className="text-2xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>让模型搜索网络获取参考信息</p>
                  </div>
                  <Toggle value={scene.video_gua_web_search ?? false} onChange={(v) => handleChange(index, "video_gua_web_search", v)} />
                </div>
              )}

              {/* 通用参数 */}
              <div className="space-y-3">
                {/* 时长 */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>时长</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-2xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)', color: '#8b5cf6' }}>
                        {effectiveGuaDuration === -1 ? '智能' : `${effectiveGuaDuration}s`}
                      </span>
                      <button onClick={() => handleChange(index, "video_gua_duration", effectiveGuaDuration === -1 ? 5 : -1)}
                        className="text-2xs px-1.5 py-0.5 rounded transition-all"
                        style={{
                          backgroundColor: effectiveGuaDuration === -1 ? '#8b5cf6' : 'var(--color-bg-secondary)',
                          color: effectiveGuaDuration === -1 ? 'white' : 'var(--color-text-muted)',
                          border: `1px solid ${effectiveGuaDuration === -1 ? '#8b5cf6' : 'var(--color-border-primary)'}`,
                        }}
                      >自动</button>
                    </div>
                  </div>
                  {effectiveGuaDuration !== -1 && (
                    <div className="flex items-center gap-3">
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>4s</span>
                      <input type="range" min={4} max={15} step={1} value={effectiveGuaDuration}
                        onChange={(e) => handleChange(index, "video_gua_duration", Number(e.target.value))}
                        className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #8b5cf6 ${(Number(effectiveGuaDuration) - 4) / 11 * 100}%, var(--color-bg-tertiary) ${(Number(effectiveGuaDuration) - 4) / 11 * 100}%)`,
                          accentColor: 'var(--color-purple-primary)',
                        }}
                      />
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>15s</span>
                    </div>
                  )}
                </div>

                {/* 分辨率 */}
                <div className="space-y-1.5">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>分辨率</span>
                  <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    {guaResolutionOptions.map((r) => (
                      <button key={r} onClick={() => handleChange(index, "video_gua_resolution", r)}
                        className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                        style={{
                          backgroundColor: currentGuaResolution === r ? 'var(--color-purple-primary)' : 'transparent',
                          color: currentGuaResolution === r ? 'white' : 'var(--color-text-secondary)',
                        }}
                      >{r}</button>
                    ))}
                  </div>
                </div>

                {/* 比例 */}
                <div className="space-y-1.5">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>宽高比</span>
                  <div className="grid grid-cols-4 gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    {["adaptive","16:9","4:3","1:1","3:4","9:16","21:9"].map((r) => (
                      <button key={r} onClick={() => handleChange(index, "video_gua_ratio", r)}
                        className="py-1.5 text-2xs rounded-lg transition-all font-medium"
                        style={{
                          backgroundColor: (scene.video_gua_ratio || "adaptive") === r ? 'var(--color-purple-primary)' : 'transparent',
                          color: (scene.video_gua_ratio || "adaptive") === r ? 'white' : 'var(--color-text-secondary)',
                        }}
                      >{r === "adaptive" ? "自适应" : r}</button>
                    ))}
                  </div>
                </div>

                {/* 有声视频 */}
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>有声视频</span>
                  <Toggle value={scene.video_gua_audio ?? true} onChange={(v) => handleChange(index, "video_gua_audio", v)} />
                </div>

                {/* 去字幕 */}
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>去字幕</span>
                  <Toggle value={scene.video_gua_no_subtitle ?? false} onChange={(v) => handleChange(index, "video_gua_no_subtitle", v)} />
                </div>
              </div>
            </div>
          )}

          {/* ═══ 万相3.0 All-in-One 专属参数 ═══ */}
          {isWanx && (
            <div className="space-y-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)' }}>
              <div className="flex items-center justify-between gap-2 pb-2" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                <span className="text-xs font-bold" style={{ color: '#6366f1' }}>万相3.0 All-in-One</span>
                <span className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: '#6366f118', color: '#6366f1' }}>{currentWanxModel}</span>
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>模型版本</span>
                <div className="grid grid-cols-2 gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {WANX_VIDEO_MODELS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleChange(index, 'video_sub_model', value)}
                      className="py-2 text-2xs rounded-lg transition-all font-bold"
                      style={{
                        backgroundColor: currentWanxModel === value ? '#6366f1' : 'transparent',
                        color: currentWanxModel === value ? 'white' : 'var(--color-text-secondary)',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>生成模式</span>
                <div className="grid grid-cols-2 gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {[
                    { value: 'text_to_video', label: '文生视频' },
                    { value: 'first_frame', label: '首帧生视频' },
                    { value: 'first_last_frame', label: '首尾帧' },
                    { value: 'reference_to_video', label: '参考生视频' },
                  ].map((item) => (
                    <button key={item.value} onClick={() => handleWanxModeChange(item.value)}
                      className="py-2 text-2xs rounded-lg transition-all font-bold"
                      style={{
                        backgroundColor: activeWanxMode === item.value ? '#6366f1' : 'transparent',
                        color: activeWanxMode === item.value ? 'white' : 'var(--color-text-secondary)',
                      }}
                    >{item.label}</button>
                  ))}
                </div>
                {activeWanxMode === 'reference_to_video' && (
                  <div className="space-y-2">
                    <p className="text-2xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                      当前分镜可使用上方角色、场景和道具参考图，也可在提示词中输入 @ 添加图片或视频。音频、文件和网页参考请在视频生成台使用。
                    </p>
                    {wanxReferenceMedia.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {wanxReferenceMedia.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-2xs"
                            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                            title={item.name || item.url}
                          >
                            <span className="max-w-36 truncate">{getWanxVideoMediaLabel(item, wanxReferenceMedia)} · {item.name || '参考素材'}</span>
                            <button
                              type="button"
                              onClick={() => removeWanxReferenceMedia(item.id)}
                              className="rounded p-0.5 hover:bg-[var(--color-bg-tertiary)]"
                              aria-label="移除参考素材"
                            >
                              <X size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>输出时长</span>
                  <button onClick={() => handleChange(index, 'video_wan_duration', (scene.video_wan_duration ?? 5) === -1 ? 5 : -1)}
                    className="text-2xs px-2 py-1 rounded transition-all"
                    style={{
                      backgroundColor: (scene.video_wan_duration ?? 5) === -1 ? '#6366f1' : 'var(--color-bg-secondary)',
                      color: (scene.video_wan_duration ?? 5) === -1 ? 'white' : 'var(--color-text-secondary)',
                      border: `1px solid ${(scene.video_wan_duration ?? 5) === -1 ? '#6366f1' : 'var(--color-border-primary)'}`,
                    }}
                  >{(scene.video_wan_duration ?? 5) === -1 ? '智能时长' : `${scene.video_wan_duration ?? 5}s`}</button>
                </div>
                {(scene.video_wan_duration ?? 5) !== -1 && (
                  <div className="flex items-center gap-3">
                    <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>2s</span>
                    <input type="range" min={2} max={30} step={1} value={scene.video_wan_duration ?? 5}
                      onChange={(event) => handleChange(index, 'video_wan_duration', Number(event.target.value))}
                      className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: '#6366f1' }}
                    />
                    <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>30s</span>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>分辨率</span>
                <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {['480P', '720P', '1080P'].map((resolution) => (
                    <button key={resolution} onClick={() => handleChange(index, 'video_wan_resolution', resolution)}
                      className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                      style={{
                        backgroundColor: (scene.video_wan_resolution || '1080P') === resolution ? '#6366f1' : 'transparent',
                        color: (scene.video_wan_resolution || '1080P') === resolution ? 'white' : 'var(--color-text-secondary)',
                      }}
                    >{resolution}</button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>宽高比</span>
                <div className="grid grid-cols-3 gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'].map((ratio) => (
                    <button key={ratio} onClick={() => handleChange(index, 'video_wan_ratio', ratio)}
                      className="py-1.5 text-2xs rounded-lg transition-all font-medium"
                      style={{
                        backgroundColor: (scene.video_wan_ratio || 'adaptive') === ratio ? '#6366f1' : 'transparent',
                        color: (scene.video_wan_ratio || 'adaptive') === ratio ? 'white' : 'var(--color-text-secondary)',
                      }}
                    >{ratio === 'adaptive' ? '自适应' : ratio}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>生成声音</span>
                  <Toggle value={scene.video_wan_audio ?? true} onChange={(value) => handleChange(index, 'video_wan_audio', value)} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>AI水印</span>
                  <Toggle value={scene.video_wan_watermark ?? false} onChange={(value) => handleChange(index, 'video_wan_watermark', value)} />
                </div>
              </div>

              <label className="space-y-1.5 block">
                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>随机种子（可选）</span>
                <input type="number" min={0} max={2147483647} value={scene.video_wan_seed ?? ''}
                  onChange={(event) => handleChange(index, 'video_wan_seed', event.target.value === '' ? null : Number(event.target.value))}
                  placeholder="留空则随机"
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                />
              </label>
            </div>
          )}

          {/* ═══ 打光预设 ═══ */}
          <div className="space-y-2">
            <SectionLabel><Lightbulb size={12} />打光预设</SectionLabel>
            <div className="grid grid-cols-3 gap-1.5">
              {LIGHTING_PRESETS.map(lp => {
                                const isSel = scene.lighting === lp.id;
                return (
                  <div key={lp.id} className="relative group">
                    <button
                      onClick={() => handleChange(index, "lighting", isSel ? null : lp.id)}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-2xs transition-all"
                      style={{
                        backgroundColor: isSel ? `${lp.color}18` : 'var(--color-bg-primary)',
                        border: isSel ? `1px solid ${lp.color}40` : '1px solid var(--color-border-secondary)',
                        color: isSel ? lp.color : 'var(--color-text-secondary)',
                      }}
                    >
                      {(() => { const LpIcon = LIGHTING_ICON_MAP[lp.id] || Lightbulb; return <LpIcon size={13} className="flex-shrink-0" />; })()}
                      <span className="truncate">{lp.name}</span>
                    </button>
                    <div className="absolute left-0 right-0 top-full mt-1 px-2 py-1.5 rounded-lg text-2xs leading-snug opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 pointer-events-none"
                      style={{ backgroundColor: 'var(--color-bg-overlay, rgba(0,0,0,0.85))', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      {lp.prompt}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 非 Vidu/WangsuSora/Kling/GuaVideo 时长选择 */}
          {!isVidu && !isWangsuSora && !isKling && !isGuaVideo && !isWanx && (
            <div className="space-y-2">
              <SectionLabel>生成时长</SectionLabel>
              <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                {(currentModel?.durations || []).map((d) => (
                  <button key={d} onClick={() => handleChange(index, "video_duration", d)}
                    className="flex-1 py-2 text-xs rounded-lg transition-all font-medium"
                    style={{
                      backgroundColor: (scene.video_duration || currentModel?.defaultDuration) === d ? theme.colors.bluePrimary : 'transparent',
                      color: (scene.video_duration || currentModel?.defaultDuration) === d ? 'white' : 'var(--color-text-secondary)',
                    }}
                  >{d}</button>
                ))}
              </div>
            </div>
          )}

          {/* ═══ 首尾帧控制 ═══ */}
          {((!isGuaVideo && (!isWanx || activeWanxMode === 'first_frame' || activeWanxMode === 'first_last_frame')) || (isGuaVideo && activeGuaMode === "i2v")) && (
          <div className="space-y-2.5">
            <SectionLabel>{isWanx && activeWanxMode === 'first_frame' ? '首帧控制' : '首尾帧控制'}</SectionLabel>
            <div className={`grid ${isWanx && activeWanxMode === 'first_frame' ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
              {/* 首帧 */}
              <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)' }}>
                <span className="text-2xs font-bold block mb-2" style={{ color: 'var(--color-text-muted)' }}>起始帧</span>
                <div
                  className="aspect-video rounded-lg overflow-hidden relative"
                  style={{ backgroundColor: 'var(--color-bg-overlay)' }}
                  onMouseEnter={(e) => handleFrameHover(e, scene.generated_image)}
                  onMouseLeave={() => setHoveredFrameImage(null)}
                >
                  {scene.generated_image ? (
                    <>
                      <img src={scene.generated_image} className="w-full h-full object-cover" alt="" />
                      <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.colors.success }}>
                        <Check size={8} strokeWidth={3} style={{ color: 'white' }} />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>未生成图片</span>
                    </div>
                  )}
                </div>
              </div>

              {(!isWanx || activeWanxMode === 'first_last_frame') && (<>
              {/* 尾帧 */}
              <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)' }}>
                <span className="text-2xs font-bold block mb-2" style={{ color: 'var(--color-text-muted)' }}>结束帧</span>
                <div className="relative">
                  <div
                    onClick={() => setIsEndFrameSelectorOpen(!isEndFrameSelectorOpen)}
                    className="aspect-video rounded-lg overflow-hidden relative cursor-pointer transition-all"
                    style={{
                      backgroundColor: 'var(--color-bg-overlay)',
                      border: `2px solid ${isEndFrameSelectorOpen ? theme.colors.bluePrimary : 'transparent'}`,
                    }}
                  >
                    {scene.video_end_shot_index !== null && scene.video_end_shot_index !== undefined && rows[scene.video_end_shot_index]?.generated_image ? (
                      <>
                        <img src={rows[scene.video_end_shot_index].generated_image} className="w-full h-full object-cover" alt="" />
                        <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-2xs font-bold" style={{ backgroundColor: 'var(--overlay-heavy)', color: theme.colors.bluePrimary }}>
                          #{rows[scene.video_end_shot_index]?.shot_number}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                        <Film size={16} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                        <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>点击选择</span>
                      </div>
                    )}
                  </div>

                  {endFrameAnim.mounted && (
                    <div
                      className={`absolute top-full left-0 w-full mt-1 rounded-lg shadow-xl z-50 max-h-40 overflow-y-auto custom-scrollbar p-1.5 ${endFrameAnim.exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-100"}`}
                      style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}
                    >
                      <div
                        onClick={() => { handleChange(index, "video_end_shot_index", null); setIsEndFrameSelectorOpen(false); }}
                        className="p-2 rounded-lg text-xs cursor-pointer flex items-center gap-2 transition-colors hover:bg-[var(--color-bg-tertiary)]"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        <X size={12} /> 不指定尾帧
                      </div>
                      {rows.map((r, i) =>
                        i !== index && r.generated_image ? (
                          <div key={i}
                            onClick={() => { handleChange(index, "video_end_shot_index", i); setIsEndFrameSelectorOpen(false); }}
                            onMouseEnter={(e) => handleFrameHover(e, r.generated_image)}
                            onMouseLeave={() => setHoveredFrameImage(null)}
                            className="flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors hover:bg-[var(--color-bg-tertiary)]"
                            style={{ backgroundColor: scene.video_end_shot_index === i ? `${theme.colors.bluePrimary}15` : 'transparent' }}
                          >
                            <div className="w-8 h-6 rounded overflow-hidden flex-shrink-0" style={{ backgroundColor: 'black' }}>
                              <img src={r.generated_image} className="w-full h-full object-cover" alt="" />
                            </div>
                            <span className="text-xs font-bold" style={{ color: scene.video_end_shot_index === i ? theme.colors.bluePrimary : 'var(--color-text-secondary)' }}>
                              #{r.shot_number}
                            </span>
                          </div>
                        ) : null
                      )}
                    </div>
                  )}
                </div>
              </div>
              </>)}
            </div>
          </div>
          )}

        </div>

        {/* ── 底部生成按钮 ── */}
        <div className="shrink-0 px-5 py-3" style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
          <button
            onClick={() => {
              if (handleGenerateVideo) handleGenerateVideo(index);
              onClose();
            }}
            disabled={scene.isGeneratingVideo}
            className="w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-40"
            style={{ backgroundColor: currentModel.accentColor || theme.colors.accentPrimary, color: 'white' }}
            onMouseEnter={(e) => { if (!scene.isGeneratingVideo) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.boxShadow = `0 4px 16px ${currentModel.accentColor || theme.colors.accentPrimary}50`; }}}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <Film size={15} />
            {scene.isGeneratingVideo ? '生成中...' : '开始生成视频'}
          </button>
        </div>

        {/* 帧预览大图 */}
        {hoveredFrameImage && ReactDOM.createPortal(
          <div
            className="fixed z-[1000] rounded-xl overflow-hidden shadow-2xl pointer-events-none animate-in fade-in duration-100"
            style={{
              top: hoveredFramePos.top,
              left: hoveredFramePos.left,
            }}
          >
            <img src={hoveredFrameImage} alt="" className="rounded-xl shadow-2xl" style={{
              maxWidth: 'min(320px, 40vw)', maxHeight: 'min(400px, 50vh)', width: 'auto', height: 'auto', objectFit: 'contain',
              backgroundColor: 'color-mix(in srgb, var(--color-bg-overlay) 95%, transparent)',
              border: '1px solid var(--color-border-primary)',
              padding: '4px',
            }} />
          </div>,
          document.body
        )}
      </div>
    </div>,
    document.body
  );
};

export default VideoSettingsPanel;
